import { FC, useEffect, useMemo, useState, useRef } from 'react'
import { canEditCrewDataAtOfficeReview } from '../core/crewOfficeEditAccess'
import {latestReviewedClaim, loadAllotmentClaimsForPeriods, resolveAllotmentCarryForward} from '../core/allotmentCarryForward'
import { canSubmitAllotmentDraft, canEditManagementAllotmentDraft } from '../core/allotmentSubmissionAccess'
import { toast, ToastContainer } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { useAuth } from '../../auth'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import {resolveCrewingActorCompanyScope} from '../core/crewingActorCompanyScope'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { Crew, Rank, Company, CompanyAdmin, Vessel } from '../core/_models'
import {
  CrewClaim,
  CrewAllotmentRecord,
  CrewBankingDetailsRecord,
  getAllotmentPeriodRoster,
  getRanks,
  getCompanyList,
  getCompanyAdminList,
  getCrewClaims,
  getCrewClaimsByMonth,
  getSignOnOffRecords,
  SignOnOffRecord,
  getCrewAllotments,
  getCrewAllotmentOverview,
  createCrewAllotment,
  updateCrewAllotment,
  submitCrewAllotment,
  saveCrewAllotmentReviewNote,
  closeCrewAllotmentSatisfactory,
  uploadCrewAllotmentAttachment,
  crewAllotmentAttachmentViewUrl,
  crewAllotmentAttachmentDownloadUrl,
  saveCrewAllotmentMasterReviewNote,
  approveCrewAllotmentMasterReview,
  bulkReviewAllotments,
  bulkMasterReviewAllotments,
} from '../core/_requests'
import AddAllotmentAdvanceModal, {
  AllotmentAdvanceDraft,
} from '../components/AddAllotmentAdvanceModal'
import ViewAllotmentAdvanceModal, {
  AllotmentAdvanceRecord,
} from '../components/ViewAllotmentAdvanceModal'
import AllotmentAdvanceReviewModal from '../components/AllotmentAdvanceReviewModal'
import type { AllotmentAdvanceWorkflowStatus } from '../components/AllotmentAdvanceReviewModal'
import { RemarksCell, RemarksModal } from '../../QHSE/components/StructuredRemarksView'
import AllotmentAdvanceMasterReviewModal from '../components/AllotmentAdvanceMasterReviewModal'
import AllotmentBulkReviewModal from '../components/AllotmentBulkReviewModal'
import AllotmentMasterBulkReviewModal from '../components/AllotmentMasterBulkReviewModal'
import CrewClaimAttachmentsModal from '../components/CrewClaimAttachmentsModal'
import SalarySlipModal, { SalarySlipCrewOption } from '../components/SalarySlipModal'
import PortageBillModal from '../components/PortageBillModal'
import CrewAllotmentExportModal from '../components/CrewAllotmentExportModal'
import { FileViewerModal } from '../../QHSE/components/FileViewerModal'
import {
  CREWING_ACTION_PERMISSIONS,
  CREWING_READ_PERMISSIONS,
  useCrewingCanRead,
  useCrewingPermissionRevision,
} from '../core/crewingPermissionAccess'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import {
  applyCrewingPresentationFilters,
  allowCompanyPresentationFilters,
  extractRows,
} from '../core/crewingPresentationFilters'
import {
  buildCrewSignOnDateMap,
  getCrewSignOnMonth,
  toSignOnMonthKey,
} from '../core/signOnDateUtils'
import {
  blurNumberInputOnWheel,
  preventNumberInputArrowStep,
} from '../../../shared/utils/numberInputGuards'

type SortKey =
  | 'crewName'
  | 'rankName'
  | 'allotmentAmount'
  | 'wagesEarned'
  | 'carryForwardAmount'
  | 'balanceCf'

type FinanceDraft = {
  carryForwardAmount: string
  wagesEarned: string
  allotmentAmount: string
  balanceBroughtForward: string
}

const DEFAULT_WORKFLOW_STATUS: AllotmentAdvanceWorkflowStatus = 'DRAFT'


const resolveBankCode = (
  type?: string | null,
  rtgsCode?: string | null,
  swiftCode?: string | null
): { label: string; value: string } => {
  const normalizedType = (type || '').toString().trim().toUpperCase()
  const rtgs = (rtgsCode || '').toString().trim()
  const swift = (swiftCode || '').toString().trim()

  if (normalizedType === 'RTGS') {
    return { label: 'IFSC / RTGS', value: rtgs || '-' }
  }

  if (normalizedType === 'IFSC') {
    return { label: 'SWIFT / IBAN', value: swift || '-' }
  }

  if (rtgs) {
    return { label: 'IFSC / RTGS', value: rtgs }
  }

  if (swift) {
    return { label: 'SWIFT / IBAN', value: swift }
  }

  return { label: '-', value: '-' }
}

const resolveCurrencyCode = (salaryCurrency?: string, salaryOtherCurrency?: string) => {
  const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
  const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
  if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
  return normalizedAccountCurrency
}

const currentMonthStartLocal = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const getCurrencySymbol = (currencyCode?: string) => {
  const code = (currencyCode || '').toString().trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '\u20AC'
    case 'GBP':
      return '\u00A3'
    case 'SGD':
      return 'S$'
    case 'INR':
      return '\u20B9'
    case 'AED':
      return '\u062F.\u0625 '
    default:
      try {
        const part = new Intl.NumberFormat('en', {
          style: 'currency',
          currency: code,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
          .formatToParts(0)
          .find((p) => p.type === 'currency')
        const maybeSymbol = (part?.value || '').trim()
        return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
      } catch {
        return ''
      }
  }
}

const getCurrentPeriod = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${now.getFullYear()}-${month}`
}

const getPreviousPeriod = (period?: string): string | undefined => {
  if (!period) return undefined
  const [yearPart, monthPart] = period.split('-')
  const year = Number(yearPart)
  const month = Number(monthPart)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return undefined

  const previous = new Date(year, month - 2, 1)
  const previousMonth = String(previous.getMonth() + 1).padStart(2, '0')
  return `${previous.getFullYear()}-${previousMonth}`
}

const formatPeriodLabel = (period?: string): string => {
  const key = toPeriodKey(period)
  if (!key) return 'selected month'

  const [yearPart, monthPart] = key.split('-')
  const year = Number(yearPart)
  const month = Number(monthPart)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return key
  }

  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

const toPeriodKey = (value?: string | null): string | undefined => {
  const raw = (value || '').toString().trim()
  if (!raw) return undefined

  const isoLikeMatch = raw.match(/^(\d{4})-(\d{2})/)
  if (isoLikeMatch) {
    const month = Number(isoLikeMatch[2])
    if (month >= 1 && month <= 12) {
      return `${isoLikeMatch[1]}-${isoLikeMatch[2]}`
    }
  }

  const parsed = new Date(raw)
  if (!Number.isNaN(parsed.getTime())) {
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    return `${parsed.getFullYear()}-${month}`
  }

  return undefined
}

const toStatementMonthStart = (period?: string): string | undefined => {
  const key = toPeriodKey(period)
  return key ? `${key}-01` : undefined
}

const filterAllotmentsByPeriod = (
  allotments: CrewAllotmentRecord[],
  period?: string
): CrewAllotmentRecord[] => {
  const key = toPeriodKey(period)
  if (!key) return allotments
  return allotments.filter((row) => toPeriodKey(row.statementMonth || undefined) === key)
}

const RANK_ORDER = [
  'MASTER',
  'CHIEF OFFICER',
  'SECOND OFFICER',
  'THIRD OFFICER',
  'DECK CADET',
  'CHIEF ENGINEER',
  'SECOND ENGINEER',
  'THIRD ENGINEER',
  'FOURTH ENGINEER',
  'TRAINEE MARINE ENGINEER',
  'ELECTRICAL OFFICER',
  'BOSUN',
  'PUMPMAN',
  'ABLE SEAMAN',
  'ORDINARY SEAMAN',
  'TR. SEAMAN',
  'OILER',
  'WIPER',
  'TR WIPER',
  'FITTER',
  'CHIEF COOK',
  'GENERAL STEWARD',
] as const

const RANK_REGEX: Record<string, RegExp[]> = {
  MASTER: [/(^|\s)master\b/i],
  'CHIEF OFFICER': [/chief\s*officer/i, /\bc\s*\/\s*o\b/i],
  'SECOND OFFICER': [/second\s*officer/i, /\b2\s*\/\s*o\b/i],
  'THIRD OFFICER': [/third\s*officer/i, /\b3\s*\/\s*o\b/i],
  'DECK CADET': [/deck\s*cadet/i],
  'CHIEF ENGINEER': [/chief\s*engineer/i, /\bc\s*\/\s*e\b/i],
  'SECOND ENGINEER': [/second\s*engineer/i, /\b2\s*\/\s*e\b/i],
  'THIRD ENGINEER': [/third\s*engineer/i, /\b3\s*\/\s*e\b/i],
  'FOURTH ENGINEER': [/fourth\s*engineer/i, /\b4\s*\/\s*e\b/i, /fouth\s*engineer/i],
  'TRAINEE MARINE ENGINEER': [/engine\s*cadet/i, /trainee\s*marine\s*engineer/i, /\btme\b/i],
  'ELECTRICAL OFFICER': [/electrician/i, /electrical/i, /\beto\b/i],
  BOSUN: [/\bbosun\b/i, /boatswain/i],
  PUMPMAN: [/pumpman/i],
  'ABLE SEAMAN': [/able\s*seaman/i, /\bab\b(?![a-z])/i],
  'ORDINARY SEAMAN': [/ordinary\s*seaman/i, /\bos\b(?![a-z])/i],
  'TR. SEAMAN': [/trainee.*seaman/i, /\btr\.?\s*seaman\b/i],
  OILER: [/oiler/i, /motorman/i],
  WIPER: [/wiper\b/i],
  'TR WIPER': [/trainee.*wiper/i, /\btr\.?\s*wiper\b/i],
  FITTER: [/fitter\b/i],
  'CHIEF COOK': [/chief\s*cook/i],
  'GENERAL STEWARD': [/steward/i, /messman/i],
}

const getCanonicalRank = (label?: string): string => {
  const value = (label ?? '').trim()
  for (const [canonical, patterns] of Object.entries(RANK_REGEX)) {
    if (patterns.some((regex) => regex.test(value))) return canonical
  }
  return 'OTHER'
}

const getRankSuffix = (label?: string): number => {
  const value = (label ?? '').trim()
  const match = value.match(/-\s*(\d+)\s*$/)
  return match ? Number(match[1]) : 0
}

const rankTuple = (label?: string): [number, number, number, string] => {
  const canonical = getCanonicalRank(label)
  const groupIndex = canonical === 'OTHER' ? 999 : RANK_ORDER.indexOf(canonical as any) + 1
  const suffix = getRankSuffix(label)
  const baseFirstFlag = suffix === 0 ? 0 : 1
  return [groupIndex, baseFirstFlag, suffix, label ?? '']
}

const compareRank = (
  aLabel?: string,
  bLabel?: string,
  direction: 'asc' | 'desc' = 'asc'
): number => {
  const tupleA = rankTuple(aLabel)
  const tupleB = rankTuple(bLabel)
  const compareValue =
    tupleA[0] - tupleB[0] ||
    tupleA[1] - tupleB[1] ||
    tupleA[2] - tupleB[2] ||
    tupleA[3].localeCompare(tupleB[3])
  return direction === 'asc' ? compareValue : -compareValue
}

type ClaimsByCrew = Record<number, { totalClaim: number; balanceCf: number; totalDeduction: number; wagesEarned: number }>
type ClaimGateByCrew = Record<number, { exists: boolean; reviewPending: boolean }>
type BankingByCrew = Record<number, CrewBankingDetailsRecord[]>
type AllotmentByAccountByCrew = Record<number, Record<string, number>>

const parseNumberOrZero = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const normalizeAccountKey = (value?: string | null): string =>
  (value || '')
    .toString()
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .trim()

const accountMatches = (left?: string | null, right?: string | null): boolean => {
  const leftKey = normalizeAccountKey(left)
  const rightKey = normalizeAccountKey(right)

  if (!leftKey || !rightKey) return false
  if (leftKey === rightKey) return true

  const leftDigits = leftKey.replace(/[^0-9]/g, '')
  const rightDigits = rightKey.replace(/[^0-9]/g, '')
  if (leftDigits && rightDigits) {
    const tailLength = Math.min(8, leftDigits.length, rightDigits.length)
    if (tailLength >= 4 && leftDigits.slice(-tailLength) === rightDigits.slice(-tailLength)) {
      return true
    }
  }

  return false
}

const isAllocationSummaryRow = (row: CrewAllotmentRecord): boolean => {
  const remarks = (row.remarks || '').toString().trim().toLowerCase()
  return remarks.startsWith('allocations -')
}

const CrewAllotment: FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth()
  const {
    isCrew,
    isCustomRole,
    isOfficeRole,
    isCompanyGroupAdmin: baseIsCompanyGroupAdmin,
    isCompanyAdmin: baseIsCompanyAdmin,
    isOperator: baseIsOperator,
  } = useCrewingRoleAccess()
  const roleId = currentUser?.role?.id ?? 0
  const roleEntityId = currentUser?.roleEntityId ?? null
  const cgaId = currentUser?.companyGroupAdminId ?? null
  const actorCompanyScope = resolveCrewingActorCompanyScope(currentUser, isCustomRole)
  const operatorCompanyAdminId =
    currentUser?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? null
  const operatorCompanyGroupAdminId =
    currentUser?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? null
  const operatorActsLikeCompanyAdmin = (baseIsOperator || roleId === 6) && operatorCompanyAdminId != null
  const operatorActsLikeGroupAdmin =
    (baseIsOperator || roleId === 6) && !operatorActsLikeCompanyAdmin && operatorCompanyGroupAdminId != null
  const operatorActsLikeSuperadmin =
    (baseIsOperator || roleId === 6) && !operatorActsLikeCompanyAdmin && operatorCompanyGroupAdminId == null
  const isCompanyGroupAdmin = baseIsCompanyGroupAdmin || roleId === 5
  const isCompanyAdmin = baseIsCompanyAdmin || roleId === 2
  const isOperator = baseIsOperator || roleId === 6
  const rankName = (currentUser?.rank?.rank ?? '').toLowerCase()
  const isMasterRank = (currentUser?.rank?.id ?? 0) === 1 || rankName.includes('master')
  const isCrewMaster = isCrew && isMasterRank
  const canMasterReview = isCrewMaster || isSuperAdmin
  const canReviewSubmitted =
    !isCrew &&
    (isSuperAdmin ||
      hasPermission('APPROVE_CREW_ALLOTMENT'))
  const canAddAllotmentAdvance = isSuperAdmin || hasPermission('CREATE_CREW_ALLOTMENT')
  const canOpenSalarySlip =
    isSuperAdmin || hasPermission('SALARY_SLIP_CREW_ALLOTMENT')
  const canOpenPortageBill =
    isSuperAdmin ||
    hasPermission('PORTAGE_BILL_CREW_ALLOTMENT') ||
    hasPermission(CREWING_ACTION_PERMISSIONS.PORTAGE_BILL_CREW_ALLOTMENT)
  const canSubmitAllotment = isSuperAdmin || hasPermission('APPROVE_CREW_ALLOTMENT')
  const hasManagementDraftPermission = hasPermission('MANAGE_CREW_ALLOTMENT_DRAFT')
  const canExportAllotment =
    isSuperAdmin ||
    hasPermission('EXPORT_CREW_ALLOTMENT') ||
    hasPermission('EXPORT_CREW_ALLOTMENT_AND_ADVANCE') ||
    hasPermission('EXPORT_ALLOTMENT') ||
    hasPermission('DOWNLOAD_CREW_ALLOTMENT')
  const canDownloadAllotment =
    isSuperAdmin ||
    hasPermission('DOWNLOAD_CREW_ALLOTMENT') ||
    hasPermission('EXPORT_CREW_ALLOTMENT') ||
    hasPermission('EXPORT_CREW_ALLOTMENT_AND_ADVANCE') ||
    hasPermission('EXPORT_ALLOTMENT')
  const useCrewDropdownFilter = isCompanyGroupAdmin || isCompanyAdmin

  const [records, setRecords] = useState<AllotmentAdvanceRecord[]>([])
  const [ranks, setRanks] = useState<Rank[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [crewRoster, setCrewRoster] = useState<Crew[]>([])
  const [rosterPeriod, setRosterPeriod] = useState<string>('')

  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  const [modalCompanyGroupId, setModalCompanyGroupId] = useState<number | ''>('')
  const [modalCompanyAdminId, setModalCompanyAdminId] = useState<number | ''>('')
  const [modalVesselId, setModalVesselId] = useState<number | ''>('')
  const [modalCrewMembers, setModalCrewMembers] = useState<Crew[]>([])

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('')
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'rankName',
    direction: 'asc',
  })

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isSalarySlipModalOpen, setIsSalarySlipModalOpen] = useState(false)
  const [isPortageBillModalOpen, setIsPortageBillModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewRecord, setViewRecord] = useState<AllotmentAdvanceRecord | null>(null)
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false)
  const [attachmentsModalRecord, setAttachmentsModalRecord] =
    useState<AllotmentAdvanceRecord | null>(null)
  const [claimsByCrewId, setClaimsByCrewId] = useState<ClaimsByCrew>({})
  const [claimGateByCrewId, setClaimGateByCrewId] = useState<ClaimGateByCrew>({})
  const [bankingByCrewId, setBankingByCrewId] = useState<BankingByCrew>({})
  const [allotmentByAccountByCrewId, setAllotmentByAccountByCrewId] =
    useState<AllotmentByAccountByCrew>({})
  const [lastMonthBalanceByCrewId, setLastMonthBalanceByCrewId] = useState<
    Record<number, number | undefined>
  >({})
  const [isRosterLoading, setIsRosterLoading] = useState(false)
  const [isAllotmentLoading, setIsAllotmentLoading] = useState(false)
  const [hasStartedRosterLoad, setHasStartedRosterLoad] = useState(false)
  const allotmentFetchSeqRef = useRef(0)
  const modalFetchSeqRef = useRef(0)
  const rankMapRef = useRef<Record<number, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [inlineDrafts, setInlineDrafts] = useState<Record<number, FinanceDraft>>({})
  const [inlineSavingCrewIds, setInlineSavingCrewIds] = useState<Record<number, boolean>>({})
  const [accountAllotmentDraftByCrewId, setAccountAllotmentDraftByCrewId] = useState<
    Record<number, Record<string, string>>
  >({})
  const [workflowStatusByCrewId, setWorkflowStatusByCrewId] = useState<
    Record<number, AllotmentAdvanceWorkflowStatus>
  >({})
  const [masterRemarksByCrewId, setMasterRemarksByCrewId] = useState<Record<number, string>>({})
  const [reviewRemarksByCrewId, setReviewRemarksByCrewId] = useState<Record<number, string>>({})
  const [isMasterReviewModalOpen, setIsMasterReviewModalOpen] = useState(false)
  const [masterReviewingRecord, setMasterReviewingRecord] = useState<AllotmentAdvanceRecord | null>(null)
  const [isMasterReviewSaving, setIsMasterReviewSaving] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [reviewingRecord, setReviewingRecord] = useState<AllotmentAdvanceRecord | null>(null)
  const [isReviewSaving, setIsReviewSaving] = useState(false)
  const [isBulkReviewModalOpen, setIsBulkReviewModalOpen] = useState(false)
  const [isBulkReviewSaving, setIsBulkReviewSaving] = useState(false)
  const [isMasterBulkReviewModalOpen, setIsMasterBulkReviewModalOpen] = useState(false)
  const [isMasterBulkReviewSaving, setIsMasterBulkReviewSaving] = useState(false)
  const [accountsModalRecord, setAccountsModalRecord] = useState<AllotmentAdvanceRecord | null>(
    null
  )
  const [fileModal, setFileModal] = useState<{
    visible: boolean
    title: string
    fileName?: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
  })
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  // Crew Status filter state
  const [crewStatusFilter, setCrewStatusFilter] = useState<'ALL' | 'SIGNED_ON' | 'SIGNED_OFF'>('ALL')
  const [crewAssignmentStatus, setCrewAssignmentStatus] = useState<Record<number, string>>({})
  const crewFetchSeqRef = useRef(0)

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedPeriod, setSelectedPeriod] = useState<string>(getCurrentPeriod())

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  useEffect(() => {
    if (globalState.vessel?.id) {
      setSelectedVesselId(Number(globalState.vessel.id))
    } else {
      setSelectedVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setSelectedCompanyGroupId(Number(globalState.company.id))
        setSelectedCompanyAdminId('')
      } else {
        setSelectedCompanyAdminId(Number(globalState.company.id))
        setSelectedCompanyGroupId('')
      }
    } else {
      setSelectedCompanyGroupId('')
      setSelectedCompanyAdminId('')
    }
  }, [globalState.vessel, globalState.company])

  const fixedCompanyGroupId = useMemo(() => {
    if (isSuperAdmin || operatorActsLikeSuperadmin) return undefined
    if (isCompanyGroupAdmin || roleId === 5) return actorCompanyScope.companyGroupId ?? actorCompanyScope.legacyEntityId
    if (isCompanyAdmin || roleId === 2) return cgaId ?? undefined
    if (operatorActsLikeCompanyAdmin) return operatorCompanyGroupAdminId ?? undefined
    if (isCrew || roleId === 4) {
      return (
        currentUser?.vessel?.companyGroupAdminId ?? currentUser?.companyGroupAdminId ?? undefined
      )
    }
    if (operatorActsLikeGroupAdmin) return operatorCompanyGroupAdminId ?? undefined
    return undefined
  }, [
    roleId,
    isCompanyGroupAdmin,
    isCompanyAdmin,
    isCrew,
    roleEntityId,
    cgaId,
    currentUser,
    operatorActsLikeSuperadmin,
    operatorActsLikeGroupAdmin,
    operatorActsLikeCompanyAdmin,
    operatorCompanyGroupAdminId,
    actorCompanyScope.companyGroupId,
    actorCompanyScope.legacyEntityId,
  ])

  const fixedCompanyAdminId = useMemo(() => {
    if (isCompanyAdmin || roleId === 2)
      return actorCompanyScope.companyAdminId ?? actorCompanyScope.legacyEntityId
    if (operatorActsLikeCompanyAdmin) return Number(operatorCompanyAdminId) || undefined
    if (isCrew || roleId === 4)
      return currentUser?.vessel?.companyAdminId ?? currentUser?.companyAdminId ?? undefined
    return undefined
  }, [roleId, isCompanyAdmin, isCrew, roleEntityId, currentUser, operatorActsLikeCompanyAdmin, operatorCompanyAdminId,
    actorCompanyScope.companyAdminId, actorCompanyScope.legacyEntityId])

  const fixedVesselId = useMemo(() => {
    if (isCrew || roleId === 4) return currentUser?.vessel?.id ?? undefined
    return undefined
  }, [isCrew, roleId, currentUser])

  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, roleId, isOperator)

  const canReadAllotment = useCrewingCanRead(CREWING_READ_PERMISSIONS.ALLOTMENT)
  const isLoading =
    (canReadAllotment && !hasStartedRosterLoad) || isRosterLoading || isAllotmentLoading
  const permissionRevision = useCrewingPermissionRevision()

  const showCompanyFilter = fixedCompanyGroupId == null
  const showSubCompanyFilter = fixedCompanyAdminId == null && !isCrew
  const showVesselFilter = fixedVesselId == null && (isSuperAdmin || isOfficeRole || isCompanyGroupAdmin || isCompanyAdmin || isOperator || operatorActsLikeGroupAdmin || operatorActsLikeCompanyAdmin)
  const showVesselAndPeriodColumns = !isCrew
  const lockAddModalCrewSelection = isCrew

  const crewSignOnDateByCrewId = useMemo(() => buildCrewSignOnDateMap(crewRoster), [crewRoster])

  const handleSelectedPeriodChange = (value: string) => {
    const nextPeriod = value || getCurrentPeriod()
    if (nextPeriod === selectedPeriod) return
    ++crewFetchSeqRef.current
    ++allotmentFetchSeqRef.current
    ++modalFetchSeqRef.current
    setCrewRoster([])
    setModalCrewMembers([])
    setRecords([])
    setRosterPeriod('')
    setIsRosterLoading(true)
    setSelectedPeriod(nextPeriod)
  }

  const ensureClaimsReviewCompleted = (
    crewId: number,
    period?: string,
    actionLabel: string = 'create allotment'
  ) => {
    const periodKey = toPeriodKey(period || selectedPeriod)
    if (!periodKey) {
      toast.error('Select a valid month before proceeding with allotment.')
      return false
    }

    const gate = claimGateByCrewId[crewId]
    if (!gate?.exists || !gate.reviewPending) {
      return true
    }

    toast.error(
      `Complete claims office review for ${formatPeriodLabel(periodKey)} before you ${actionLabel}.`
    )
    return false
  }

  const openAddModal = () => {
    if (!canAddAllotmentAdvance) return
    setModalCompanyGroupId(selectedCompanyGroupId)
    setModalCompanyAdminId(selectedCompanyAdminId)
    setModalVesselId(selectedVesselId)
    setIsAddModalOpen(true)
  }

  const openSalarySlipModal = () => {
    if (!canOpenSalarySlip) return
    setIsSalarySlipModalOpen(true)
  }

  useEffect(() => {
    fetchRanks()
    fetchCompanies()
    fetchCompanyAdmins()
    fetchVessels()
  }, [])

  useEffect(() => {
    if (!canReadAllotment) return
    fetchSignOnOff()
    fetchCrewRoster(
      allowCompanyFilters
        ? {
          companyGroupId: selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined,
          companyAdminId: selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined,
          vesselId: fixedVesselId ?? (selectedVesselId ? Number(selectedVesselId) : undefined),
        }
        : {
          vesselId: fixedVesselId ?? (selectedVesselId ? Number(selectedVesselId) : undefined),
        }
    )
  }, [
    canReadAllotment,
    permissionRevision,
    allowCompanyFilters,
    selectedCompanyGroupId,
    selectedCompanyAdminId,
    selectedVesselId,
    fixedVesselId,
    selectedPeriod,
  ])

  useEffect(() => {
    if (fixedCompanyGroupId != null) setSelectedCompanyGroupId(fixedCompanyGroupId)
    if (fixedCompanyAdminId != null) setSelectedCompanyAdminId(fixedCompanyAdminId)
    if (fixedVesselId != null) setSelectedVesselId(fixedVesselId)
  }, [fixedCompanyGroupId, fixedCompanyAdminId, fixedVesselId])

  const fetchRanks = async () => {
    try {
      const data = await getRanks()
      setRanks(data)
    } catch (error) {
      console.error('Error fetching ranks:', error)
    }
  }

  const fetchCompanies = async () => {
    try {
      const data = await getCompanyList()
      setCompanies(data)
    } catch (error) {
      console.error('Error fetching companies:', error)
    }
  }

  const fetchCompanyAdmins = async () => {
    try {
      const data = await getCompanyAdminList()
      setCompanyAdmins(data)
    } catch (error) {
      console.error('Error fetching company admins:', error)
    }
  }

  const fetchVessels = async () => {
    try {
      const data = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
      setVessels(data)
    } catch (error) {
      console.error('Error fetching vessels:', error)
    }
  }

  const fetchSignOnOff = async () => {
    try {
      const data = await getSignOnOffRecords()
      const records = extractRows<SignOnOffRecord>(data || [])
      const latestRecordByCrew = new Map<number, SignOnOffRecord>()
      for (const record of records) {
        const crewId = Number(record.crewId || 0)
        if (!crewId) continue
        const existing = latestRecordByCrew.get(crewId)
        if (!existing || record.id > existing.id) {
          latestRecordByCrew.set(crewId, record)
        }
      }
      const statusMap: Record<number, string> = {}
      latestRecordByCrew.forEach((record, crewId) => {
        statusMap[crewId] = record.status
      })
      setCrewAssignmentStatus(statusMap)
    } catch (error) {
      console.error('Error fetching sign on/off records:', error)
    }
  }

  const fetchModalCrewMembers = async (filters?: {
    companyGroupId?: number
    companyAdminId?: number
    vesselId?: number
  }) => {
    const fetchSeq = ++modalFetchSeqRef.current
    setModalCrewMembers([])
    try {
      const data = await getAllotmentPeriodRoster(selectedPeriod, filters)
      if (fetchSeq !== modalFetchSeqRef.current) return
      const scopedRows = applyCrewViewAccess(data)
      if (isCrew || roleId === 4) {
        setModalCrewMembers(applySelfCrewOnlyAccess(scopedRows))
        return
      }
      setModalCrewMembers(scopedRows)
    } catch (error) {
      if (fetchSeq !== modalFetchSeqRef.current) return
      console.error('Error fetching crew members for modal:', error)
      showErrorToast(error, 'Failed to fetch crew members', 'load')
    }
  }

  const fetchCrewRoster = async (filters?: {
    companyGroupId?: number
    companyAdminId?: number
    vesselId?: number
  }) => {
    const fetchSeq = ++crewFetchSeqRef.current
    ++allotmentFetchSeqRef.current
    setHasStartedRosterLoad(true)
    setIsRosterLoading(true)
    try {
      const data = await getAllotmentPeriodRoster(selectedPeriod, filters)
      if (fetchSeq === crewFetchSeqRef.current) {
        setCrewRoster(applyCrewViewAccess(data))
        setRosterPeriod(selectedPeriod)
      }
    } catch (error) {
      if (fetchSeq !== crewFetchSeqRef.current) return
      console.error('Error fetching crew roster:', error)
      showErrorToast(error, 'Failed to fetch crew roster', 'load')
      setIsAllotmentLoading(false)
    } finally {
      if (fetchSeq === crewFetchSeqRef.current) setIsRosterLoading(false)
    }
  }

  const applyCrewViewAccess = (rows: Crew[]): Crew[] => {
    if (!isCrew && roleId !== 4) return rows

    const myVesselId = currentUser?.vessel?.id
    const byVessel = myVesselId
      ? rows.filter((crew) => (crew.vessel?.id || crew.vesselId) === myVesselId)
      : rows

    if (isCrewMaster) return byVessel

    const selfCrewId = roleEntityId ? Number(roleEntityId) : undefined
    if (selfCrewId) {
      return byVessel.filter((crew) => crew.id === selfCrewId)
    }

    const myUsername = (currentUser?.username || '').trim().toLowerCase()
    return byVessel.filter((crew) => (crew.username || '').trim().toLowerCase() === myUsername)
  }

  const applySelfCrewOnlyAccess = (rows: Crew[]): Crew[] => {
    const selfCrewId = roleEntityId ? Number(roleEntityId) : undefined
    if (selfCrewId && Number.isFinite(selfCrewId)) {
      const byId = rows.filter((crew) => Number(crew.id) === selfCrewId)
      if (byId.length > 0) return byId
    }

    const myUsername = (currentUser?.username || '').trim().toLowerCase()
    if (!myUsername) return []

    return rows.filter((crew) => (crew.username || '').trim().toLowerCase() === myUsername)
  }

  const resolveSelfCrewId = (): number | undefined => {
    const selfCrewId = roleEntityId ? Number(roleEntityId) : undefined
    if (selfCrewId && Number.isFinite(selfCrewId)) return selfCrewId

    const myUsername = (currentUser?.username || '').trim().toLowerCase()
    if (!myUsername) return undefined

    const fromModal = modalCrewMembers.find(
      (crew) => (crew.username || '').trim().toLowerCase() === myUsername
    )
    if (fromModal?.id) return Number(fromModal.id)

    const fromRoster = crewRoster.find(
      (crew) => (crew.username || '').trim().toLowerCase() === myUsername
    )
    return fromRoster?.id ? Number(fromRoster.id) : undefined
  }

  const canCrewEditRecord = (record: AllotmentAdvanceRecord): boolean => {
    if (isSuperAdmin) return true
    if (!hasPermission('UPDATE_CREW_ALLOTMENT')) return false
    if (!isCrew && roleId !== 4) return false

    const selfCrewId = roleEntityId ? Number(roleEntityId) : undefined
    if (selfCrewId != null) {
      return record.crewId === selfCrewId
    }

    const myUsername = (currentUser?.username || '').trim().toLowerCase()
    if (!myUsername) return false

    const selfCrew = crewRoster.find(
      (crew) => (crew.username || '').trim().toLowerCase() === myUsername
    )
    return selfCrew?.id != null && record.crewId === Number(selfCrew.id)
  }

  const canEditAllotmentRecord = (record: AllotmentAdvanceRecord): boolean => {
    const stage = getWorkflowStatus(record)
    if (stage === 'DRAFT') return canCrewEditRecord(record) || canEditManagementAllotmentDraft({
      isDraft: true,
      isOfficeUser: !isCrew && roleId !== 4,
      hasEditPermission: hasPermission('UPDATE_CREW_ALLOTMENT'),
      hasManagementPermission: hasManagementDraftPermission,
      canManageAllotmentDraft: record.canManageAllotmentDraft,
    })
    return stage === 'MASTER_REVIEWED' && canEditCrewDataAtOfficeReview(
      {isSuperAdmin, isCompanyAdmin: baseIsCompanyAdmin,
        isCompanyGroupAdmin: baseIsCompanyGroupAdmin, isOperator},
      hasPermission('UPDATE_CREW_ALLOTMENT'), canReviewSubmitted
    )
  }

  const isRecordOwner = (record: AllotmentAdvanceRecord): boolean => {
    if (isSuperAdmin) return true
    if (!isCrew && roleId !== 4) return false

    const selfCrewId = roleEntityId ? Number(roleEntityId) : undefined
    if (selfCrewId != null) {
      return record.crewId === selfCrewId
    }

    const myUsername = (currentUser?.username || '').trim().toLowerCase()
    if (!myUsername) return false

    const selfCrew = crewRoster.find(
      (crew) => (crew.username || '').trim().toLowerCase() === myUsername
    )
    return selfCrew?.id != null && record.crewId === Number(selfCrew.id)
  }

  const numberOrUndefined = (value: unknown): number | undefined => {
    if (value == null || value === '') return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const canSubmitAllotmentRecord = (record: AllotmentAdvanceRecord): boolean =>
    canSubmitAllotmentDraft({
      isDraft: getWorkflowStatus(record) === 'DRAFT',
      hasSubmitPermission: canSubmitAllotment,
      isRecordOwner: isRecordOwner(record),
      isOfficeUser: !isCrew && roleId !== 4,
      recordId: record.id,
      hasManagementPermission: hasManagementDraftPermission,
      canManageAllotmentDraft: record.canManageAllotmentDraft,
    })

  const toDraft = (record: AllotmentAdvanceRecord): FinanceDraft => ({
    carryForwardAmount:
      record.carryForwardAmount == null || Number.isNaN(Number(record.carryForwardAmount))
        ? ''
        : String(record.carryForwardAmount),
    wagesEarned:
      record.wagesEarned == null || Number.isNaN(Number(record.wagesEarned))
        ? ''
        : String(record.wagesEarned),
    allotmentAmount:
      record.allotmentAmount == null || Number.isNaN(Number(record.allotmentAmount))
        ? ''
        : String(record.allotmentAmount),
    balanceBroughtForward:
      record.balanceBroughtForward == null || Number.isNaN(Number(record.balanceBroughtForward))
        ? ''
        : String(record.balanceBroughtForward),
  })

  const fetchAllotmentsForCrew = async (crewList: Crew[]) => {
    const fetchSeq = ++allotmentFetchSeqRef.current
    const selectedPeriodKey = toPeriodKey(selectedPeriod)
    const eligibleCrewList = crewList.filter((crew) => {
      const signOnMonth = toSignOnMonthKey(crew.latestSignOnDate)
      if (!selectedPeriodKey || !signOnMonth) return true
      return selectedPeriodKey >= signOnMonth
    })

    if (eligibleCrewList.length === 0) {
      setIsAllotmentLoading(false)
      setRecords([])
      setInlineDrafts({})
      setClaimsByCrewId({})
      setClaimGateByCrewId({})
      setBankingByCrewId({})
      setAllotmentByAccountByCrewId({})
      setLastMonthBalanceByCrewId({})
      return
    }
    setIsAllotmentLoading(true)
    try {
      const previousPeriod = getPreviousPeriod(selectedPeriod)
      const [claims, overviewRows] = await Promise.all([
        loadAllotmentClaimsForPeriods([selectedPeriod, previousPeriod], getCrewClaimsByMonth,
          getCrewClaims).catch(() => [] as CrewClaim[]),
        getCrewAllotmentOverview(
          eligibleCrewList.map((crew) => crew.id),
          selectedPeriod,
          previousPeriod
        ),
      ])
      const claimsInPeriod = selectedPeriod
        ? claims.filter((claim) => (claim.claimMonth || '').startsWith(selectedPeriod))
        : claims
      // Only the latest monthly claim per crew should drive allotment balances.
      const latestClaimByCrew = claimsInPeriod.reduce<Record<number, CrewClaim>>((acc, claim) => {
        const crewId = Number(claim.crewId)
        if (!Number.isFinite(crewId)) return acc
        const existing = acc[crewId]
        if (!existing || parseNumberOrZero(claim.id) > parseNumberOrZero(existing.id)) {
          acc[crewId] = claim
        }
        return acc
      }, {})

      const claimGateMap = Object.values(latestClaimByCrew).reduce<ClaimGateByCrew>(
        (acc, claim) => {
          const crewId = Number(claim.crewId)
          if (!Number.isFinite(crewId)) return acc

          const status = (claim.status || '').toString().toUpperCase()
          acc[crewId] = {
            exists: true,
            reviewPending: status !== 'REVIEWED' && status !== 'REJECTED',
          }
          return acc
        },
        {}
      )

      const claimsMap = Object.values(latestClaimByCrew).reduce<ClaimsByCrew>((acc, claim) => {
        const crewId = Number(claim.crewId)
        if (!Number.isFinite(crewId)) return acc

        // Only office-reviewed claims should flow into allotment balances.
        const isOfficeReviewed = claim.status === 'REVIEWED' || Boolean(claim.reviewedAt)
        const isRejected = claim.status === 'REJECTED'
        if (!isOfficeReviewed) {
          return acc
        }

        const totalDeduction = parseNumberOrZero((claim as any).deductionsTotal)

        // For rejected claims, keep only the remaining wages logic:
        // B/F Amount + Wages Earned - Crew Deduction.
        if (isRejected) {
          const bfAmount = parseNumberOrZero((claim as any).bfAmount)
          const wagesEarned = parseNumberOrZero((claim as any).wagesEarned)
          const remainingBalance = Math.max(0, bfAmount + wagesEarned - totalDeduction)

          acc[crewId] = {
            totalClaim: 0,
            balanceCf: remainingBalance,
            totalDeduction,
            wagesEarned,
          }
          return acc
        }

        const totalClaim = parseNumberOrZero(claim.totalAmount)
        const rawBalanceCf = (claim as any).balanceCf
        const claimBalanceCf =
          rawBalanceCf == null || Number.isNaN(Number(rawBalanceCf)) ? null : Number(rawBalanceCf)
        const wagesEarned = parseNumberOrZero((claim as any).wagesEarned)
        acc[crewId] = {
          totalClaim,
          balanceCf: claimBalanceCf ?? totalClaim,
          totalDeduction,
          wagesEarned,
        }
        return acc
      }, {})

      const nextBankingByCrewId: BankingByCrew = {}
      const nextAllotmentByAccountByCrewId: AllotmentByAccountByCrew = {}
      const nextLastMonthBalanceByCrewId: Record<number, number | undefined> = {}
      const overviewByCrewId = new Map(overviewRows.map((row) => [row.crewId, row]))

      const rows = await Promise.all(
        eligibleCrewList.map(async (crew) => {
          const overview = overviewByCrewId.get(crew.id)
          const allotments = overview?.allotments ?? []
          const bankingDetails = overview?.bankingDetails ?? []
          const previousPeriodAllotments = overview?.previousPeriodAllotments ?? []

          const scopedAllotments = filterAllotmentsByPeriod(allotments, selectedPeriod)
          const scopedPreviousPeriodAllotments = filterAllotmentsByPeriod(
            previousPeriodAllotments,
            previousPeriod
          )

          const sortedBankingDetails = [...bankingDetails].sort(
            (a, b) => (Number(a.id) || 0) - (Number(b.id) || 0)
          )
          nextBankingByCrewId[crew.id] = sortedBankingDetails

          // Keep only the latest allotment row per account for the selected period.
          const accountWiseAmounts: Record<string, number> = {}
          const latestFirstAllotments = [...scopedAllotments].sort(
            (a, b) => (Number(b.allotmentId) || 0) - (Number(a.allotmentId) || 0)
          )
          latestFirstAllotments.forEach((row) => {
            if (isAllocationSummaryRow(row)) return
            const matchedBanking = sortedBankingDetails.find((bank) =>
              accountMatches(bank.accountNumber, row.bankAccount)
            )
            const key = normalizeAccountKey(matchedBanking?.accountNumber || row.bankAccount)
            if (!key || Object.prototype.hasOwnProperty.call(accountWiseAmounts, key)) return
            accountWiseAmounts[key] = parseNumberOrZero(row.allotmentAmount ?? row.amount)
          })
          nextAllotmentByAccountByCrewId[crew.id] = accountWiseAmounts

          const latestPreviousAllotment = [...scopedPreviousPeriodAllotments].sort(
            (a, b) => (Number(b.allotmentId) || 0) - (Number(a.allotmentId) || 0)
          )[0]
          nextLastMonthBalanceByCrewId[crew.id] = latestPreviousAllotment
            ? resolveAllotmentCarryForward(latestPreviousAllotment,
                latestReviewedClaim(claims, crew.id, previousPeriod)) : undefined

          const latestAllotment = [...scopedAllotments].sort(
            (a, b) => (Number(b.allotmentId) || 0) - (Number(a.allotmentId) || 0)
          )[0]
          const primaryBanking = sortedBankingDetails[0] ?? null
          const allotmentBanking = latestAllotment?.bankAccount
            ? sortedBankingDetails.find((bank) =>
              accountMatches(bank.accountNumber, latestAllotment.bankAccount)
            ) ?? null
            : null
          const activeBankingForRow = allotmentBanking ?? primaryBanking

          const crewAny = crew as any
          const normalizedCrewRankName =
            typeof crewAny?.rankName === 'string' ? crewAny.rankName.trim() : ''
          const normalizedNestedRankName =
            typeof crewAny?.rank?.rank === 'string' ? crewAny.rank.rank.trim() : ''
          const resolvedRankId = Number(crew.rankId ?? crewAny?.rank?.id ?? 0)
          const fallbackSelfRankName =
            (isCrew || roleId === 4) && Number(roleEntityId ?? 0) === Number(crew.id)
              ? (currentUser?.rank?.rank || '').trim()
              : ''
          const resolvedRankName =
            normalizedCrewRankName ||
            normalizedNestedRankName ||
            (resolvedRankId > 0 ? rankMapRef.current[resolvedRankId] : '') ||
            fallbackSelfRankName ||
            undefined

          return {
            id: latestAllotment?.allotmentId ?? 0,
            allotmentExists: Boolean(latestAllotment?.allotmentId),
            canManageAllotmentDraft: overview?.canManageAllotmentDraft === true,
            crewId: crew.id,
            crewName: crew.name,
            rankName: resolvedRankName,
            vesselName: crew.vessel?.fleet_name,
            crewActive: crew.active,
            companyGroupAdminId: crew.companyGroupAdminId,
            companyAdminId: crew.companyAdminId,
            vesselId: crew.vessel?.id || crew.vesselId,
            beneficiaryName:
              latestAllotment?.beneficiaryName ||
              activeBankingForRow?.nameOfBeneficiary ||
              crew.name,
            bankName: latestAllotment?.bankName || activeBankingForRow?.bankName || undefined,
            accountNumber:
              latestAllotment?.bankAccount || activeBankingForRow?.accountNumber || undefined,
            branchName: activeBankingForRow?.branchName || undefined,
            branchCode: activeBankingForRow?.branchCode || undefined,
            ifscRtgsType: activeBankingForRow?.ifscRtgsType || undefined,
            ifscRtgsCode: activeBankingForRow?.ifscRtgsCode || undefined,
            swiftIandi: activeBankingForRow?.swiftIandi || undefined,
            bankAddress: activeBankingForRow?.bankAddress || undefined,
            salaryCurrency:
              activeBankingForRow?.accountCurrency || activeBankingForRow?.salaryCurrency || undefined,
            salaryOtherCurrency:
              activeBankingForRow?.otherCurrency ||
              activeBankingForRow?.salaryOtherCurrency ||
              undefined,
            carryForwardAmount: numberOrUndefined(latestAllotment?.carryForwardAmount),
            wagesEarned: claimsMap[crew.id]?.wagesEarned ?? numberOrUndefined(latestAllotment?.wagesEarned),
            allotmentAmount: numberOrUndefined(
              latestAllotment?.allotmentAmount ?? latestAllotment?.amount
            ),
            balanceBroughtForward: numberOrUndefined(latestAllotment?.balanceBroughtForward),
            remarks: latestAllotment?.remarks || undefined,
            statementMonth:
              latestAllotment?.statementMonth || toStatementMonthStart(selectedPeriod),
            workflowStatus:
              (latestAllotment?.workflowStatus as AllotmentAdvanceWorkflowStatus | undefined) ??
              DEFAULT_WORKFLOW_STATUS,
            officeRemarks: latestAllotment?.officeRemarks || '',
            masterRemarks: latestAllotment?.masterRemarks || '',
            attachments: latestAllotment?.attachments || [],
            attachmentCount:
              latestAllotment?.attachmentCount ?? latestAllotment?.attachments?.length ?? 0,
          } as AllotmentAdvanceRecord
        })
      )

      if (fetchSeq !== allotmentFetchSeqRef.current) return
      setRecords(rows)
      setClaimsByCrewId(claimsMap)
      setClaimGateByCrewId(claimGateMap)
      setBankingByCrewId(nextBankingByCrewId)
      setAllotmentByAccountByCrewId(nextAllotmentByAccountByCrewId)
      setLastMonthBalanceByCrewId(nextLastMonthBalanceByCrewId)
      setInlineDrafts(() => {
        const next: Record<number, FinanceDraft> = {}
        rows.forEach((row) => {
          next[row.crewId] = toDraft(row)
        })
        return next
      })
      setWorkflowStatusByCrewId(() => {
        const next: Record<number, AllotmentAdvanceWorkflowStatus> = {}
        rows.forEach((row) => {
          next[row.crewId] = row.workflowStatus ?? DEFAULT_WORKFLOW_STATUS
        })
        return next
      })
      setMasterRemarksByCrewId(() => {
        const next: Record<number, string> = {}
        rows.forEach((row) => {
          next[row.crewId] = row.masterRemarks || ''
        })
        return next
      })
      setReviewRemarksByCrewId(() => {
        const next: Record<number, string> = {}
        rows.forEach((row) => {
          next[row.crewId] = row.officeRemarks || ''
        })
        return next
      })
    } catch (error) {
      if (fetchSeq !== allotmentFetchSeqRef.current) return
      console.error('Error fetching allotments:', error)
      showErrorToast(error, 'Failed to fetch allotments', 'load')
      setRecords([])
      setInlineDrafts({})
      setClaimsByCrewId({})
      setClaimGateByCrewId({})
      setBankingByCrewId({})
      setAllotmentByAccountByCrewId({})
      setLastMonthBalanceByCrewId({})
    } finally {
      if (fetchSeq === allotmentFetchSeqRef.current) setIsAllotmentLoading(false)
    }
  }

  const rankMap = useMemo(() => {
    const map: Record<number, string> = {}
    ranks.forEach((rank) => {
      map[rank.id] = rank.rank
    })
    return map
  }, [ranks])
  rankMapRef.current = rankMap

  // Rank names can arrive after the financial rows. Fill them in without refetching every crew.
  useEffect(() => {
    if (Object.keys(rankMap).length === 0) return
    const crewById = new Map(crewRoster.map((crew) => [crew.id, crew]))
    setRecords((previous) => {
      let changed = false
      const next = previous.map((record) => {
        if (record.rankName) return record
        const crew = crewById.get(record.crewId)
        const rankId = Number(crew?.rankId ?? (crew as any)?.rank?.id ?? 0)
        const rankName = rankMap[rankId]
        if (!rankName) return record
        changed = true
        return { ...record, rankName }
      })
      return changed ? next : previous
    })
  }, [rankMap, crewRoster])

  useEffect(() => {
    if (!isAddModalOpen) return
    fetchModalCrewMembers({
      companyGroupId:
        fixedCompanyGroupId ?? (modalCompanyGroupId ? Number(modalCompanyGroupId) : undefined),
      companyAdminId:
        fixedCompanyAdminId ?? (modalCompanyAdminId ? Number(modalCompanyAdminId) : undefined),
      vesselId: fixedVesselId ?? (modalVesselId ? Number(modalVesselId) : undefined),
    })
  }, [
    isAddModalOpen,
    modalCompanyGroupId,
    modalCompanyAdminId,
    modalVesselId,
    fixedCompanyGroupId,
    fixedCompanyAdminId,
    fixedVesselId,
    selectedPeriod,
  ])

  useEffect(() => {
    if (fixedCompanyGroupId != null) setModalCompanyGroupId(fixedCompanyGroupId)
    if (fixedCompanyAdminId != null) setModalCompanyAdminId(fixedCompanyAdminId)
    if (fixedVesselId != null) setModalVesselId(fixedVesselId)
  }, [fixedCompanyGroupId, fixedCompanyAdminId, fixedVesselId])

  useEffect(() => {
    if (rosterPeriod !== selectedPeriod) return
    fetchAllotmentsForCrew(crewRoster)
  }, [crewRoster, selectedPeriod, rosterPeriod])

  const filteredCompanyAdmins = useMemo(() => {
    const groupId =
      fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined)
    if (!companies) return []
    if (!groupId) return companies
    return companies.filter((ca) => ca.cga?.id === groupId)
  }, [companies, selectedCompanyGroupId, fixedCompanyGroupId])

  const shouldShowSubCompanyFilterField = useMemo(() => {
    if (!showSubCompanyFilter) return false
    const effectiveGroupId =
      fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined)
    if (!effectiveGroupId) return true
    return filteredCompanyAdmins.length > 0
  }, [showSubCompanyFilter, fixedCompanyGroupId, selectedCompanyGroupId, filteredCompanyAdmins])

  const filteredModalCompanyAdmins = useMemo(() => {
    const groupId =
      fixedCompanyGroupId ?? (modalCompanyGroupId ? Number(modalCompanyGroupId) : undefined)
    if (!companies) return []
    if (!groupId) return companies
    return companies.filter((ca) => ca.cga?.id === groupId)
  }, [companies, modalCompanyGroupId, fixedCompanyGroupId])

  const shouldShowModalSubCompanyFilterField = useMemo(() => {
    if (!showSubCompanyFilter) return false
    const effectiveGroupId =
      fixedCompanyGroupId ?? (modalCompanyGroupId ? Number(modalCompanyGroupId) : undefined)
    if (!effectiveGroupId) return true
    return filteredModalCompanyAdmins.length > 0
  }, [showSubCompanyFilter, fixedCompanyGroupId, modalCompanyGroupId, filteredModalCompanyAdmins])

  const filteredVessels = useMemo(() => {
    const groupId =
      fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined)
    const adminId =
      fixedCompanyAdminId ?? (selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined)
    if (!vessels) return []

    const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
      v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined

    const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
      (v as any).companyGroupAdminId ??
      (v as any).companyGroupAdmin?.id ??
      (v.companyAdmin as any)?.cgaid?.id ??
      (v.companyAdmin as any)?.cga?.id

    if (adminId) {
      return vessels.filter((v) => getVesselCompanyAdminId(v) === adminId)
    }
    if (groupId) {
      return vessels.filter((v) => getVesselCompanyGroupId(v) === groupId)
    }
    return vessels
  }, [
    vessels,
    selectedCompanyAdminId,
    selectedCompanyGroupId,
    fixedCompanyGroupId,
    fixedCompanyAdminId,
  ])

  const crewFilterOptions = useMemo(() => {
    const visibleRecords = canReadAllotment ? records : []
    const uniqueById = new Map<number, string>()

    applyCrewingPresentationFilters(visibleRecords, {
      selectedVesselId: fixedVesselId ?? selectedVesselId,
      allowCompanyPresentationFilters: allowCompanyFilters,
      selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
      selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
    }).forEach((record) => {
      const crewId = Number(record.crewId)
      if (!uniqueById.has(crewId)) {
        uniqueById.set(crewId, String(record.crewName || `Crew ${crewId}`))
      }
    })

    return Array.from(uniqueById.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [
    records,
    canReadAllotment,
    selectedCompanyGroupId,
    selectedCompanyAdminId,
    selectedVesselId,
    fixedVesselId,
    allowCompanyFilters,
  ])

  useEffect(() => {
    if (!selectedCrewId) return
    const exists = crewFilterOptions.some((crew) => crew.id === Number(selectedCrewId))
    if (!exists) setSelectedCrewId('')
  }, [selectedCrewId, crewFilterOptions])

  const modalEligibleCrewMembers = useMemo(() => {
    const periodKey = toPeriodKey(selectedPeriod)
    if (!periodKey) return modalCrewMembers

    return modalCrewMembers.filter((crew) => {
      const signOnMonth = toSignOnMonthKey(crew.latestSignOnDate)
      if (!signOnMonth) return true
      return periodKey >= signOnMonth
    })
  }, [modalCrewMembers, selectedPeriod])

  const filteredRecords = useMemo(() => {
    if (!canReadAllotment) return []

    return applyCrewingPresentationFilters(records, {
      searchTerm: useCrewDropdownFilter ? undefined : searchTerm,
      selectedCrewId: useCrewDropdownFilter ? selectedCrewId : undefined,
      selectedVesselId: fixedVesselId ?? selectedVesselId,
      allowCompanyPresentationFilters: allowCompanyFilters,
      selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
      selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
    }, {
      extraPredicate: (record) => {
        const typed = record
        const selectedPeriodKey = toPeriodKey(selectedPeriod)
        const crewSignOnMonth = getCrewSignOnMonth(typed.crewId, crewSignOnDateByCrewId)
        const isBeforeCrewSignOn = !!(
          selectedPeriodKey &&
          crewSignOnMonth &&
          selectedPeriodKey < crewSignOnMonth
        )
        const term = searchTerm.trim().toLowerCase()
        const matchesCrew = !selectedCrewId || typed.crewId === Number(selectedCrewId)
        const matchesSearch =
          (typed.crewName || '').toLowerCase().includes(term) ||
          (typed.rankName || '').toLowerCase().includes(term)
        const matchesCrewStatus = crewStatusFilter === 'ALL' || crewAssignmentStatus[typed.crewId] === crewStatusFilter
        return !isBeforeCrewSignOn && (useCrewDropdownFilter ? matchesCrew : matchesSearch) && matchesCrewStatus
      },
    })
  }, [
    records,
    canReadAllotment,
    searchTerm,
    selectedCrewId,
    selectedCompanyGroupId,
    selectedCompanyAdminId,
    selectedVesselId,
    fixedVesselId,
    allowCompanyFilters,
    useCrewDropdownFilter,
    selectedPeriod,
    crewSignOnDateByCrewId,
    crewStatusFilter,
    crewAssignmentStatus,
  ])

  const salarySlipCrewOptions = useMemo<SalarySlipCrewOption[]>(() => {
    const unique = new Map<number, SalarySlipCrewOption>()

    filteredRecords.forEach((record) => {
      if (!unique.has(record.crewId)) {
        unique.set(record.crewId, {
          crewId: record.crewId,
          crewName: record.crewName,
          rankName: record.rankName,
          vesselName: record.vesselName,
        })
      }
    })

    return Array.from(unique.values()).sort((a, b) => a.crewName.localeCompare(b.crewName))
  }, [filteredRecords])

  const sortedRecords = useMemo(() => {
    const sorted = [...filteredRecords]
    sorted.sort((a, b) => {
      const rankCompare = compareRank(a.rankName, b.rankName, 'asc')
      if (rankCompare !== 0) return rankCompare

      const { key, direction } = sortConfig
      if (key === 'rankName') {
        return 0
      }
      const getValue = (record: AllotmentAdvanceRecord) => {
        if (key === 'crewName') return record.crewName
        if (key === 'allotmentAmount') return getTotalAllotmentAmount(record)
        if (key === 'wagesEarned') return getWagesEarned(record)
        if (key === 'balanceCf') return getNetBalanceWages(record)
        return getBalanceCfLastMonth(record) ?? 0
      }
      const aVal = getValue(a)
      const bVal = getValue(b)
      if (aVal < bVal) return direction === 'asc' ? -1 : 1
      if (aVal > bVal) return direction === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [filteredRecords, sortConfig])

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedRecords.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.max(1, Math.ceil(sortedRecords.length / rowsPerPage))

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => ({
      key,
      direction:
        key === 'rankName' ? 'asc' : prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    const container = event.currentTarget
    if (container.scrollWidth <= container.clientWidth) return

    // Keep vertical scrolling native; use Shift + wheel for horizontal intent.
    const isShiftHorizontalIntent =
      event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
    if (!isShiftHorizontalIntent || event.deltaY === 0) return

    const isScrollingRight = event.deltaY > 0
    const canScrollContainerRight =
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1
    const canScrollContainerLeft = container.scrollLeft > 0
    if (
      (isScrollingRight && !canScrollContainerRight) ||
      (!isScrollingRight && !canScrollContainerLeft)
    )
      return

    event.preventDefault()
    container.scrollLeft += event.deltaY
  }

  const formatCurrency = (value?: number) =>
    typeof value === 'number'
      ? value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '-'

  const amountHeaderStyle: React.CSSProperties = { minWidth: '170px' }
  const amountGroupStyle: React.CSSProperties = { minWidth: '130px' }
  const amountPrefixStyle: React.CSSProperties = {
    minWidth: '42px',
    justifyContent: 'center',
    padding: '0.375rem 0.65rem',
  }

  const formatStatementMonth = (value?: string) => {
    if (!value) return '-'
    const [yearPart, monthPart] = value.split('-')
    const year = Number(yearPart)
    const month = Number(monthPart)
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return value
    return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' })
  }

  const parseAmount = (value: string | number | null | undefined): number => {
    if (value == null || value === '') return 0
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const parseAllocationSummary = (remarks?: string | null): Record<number, number> => {
    const result: Record<number, number> = {}
    const source = (remarks || '').toString()
    const regex = /Acc\s*(\d+)\s*:\s*([0-9]+(?:\.[0-9]+)?)/gi
    let match = regex.exec(source)
    while (match) {
      const index = Number(match[1])
      const amount = Number(match[2])
      if (Number.isFinite(index) && index > 0 && Number.isFinite(amount)) {
        result[index - 1] = amount
      }
      match = regex.exec(source)
    }
    return result
  }

  const getInlineDraft = (record: AllotmentAdvanceRecord): FinanceDraft =>
    inlineDrafts[record.crewId] ?? toDraft(record)

  const getAccountDraftKey = (account: CrewBankingDetailsRecord, index: number): string => {
    const accountKey = normalizeAccountKey(account.accountNumber)
    return accountKey || `ACC_${account.id ?? index + 1}`
  }

  const getInitialAccountAmountMap = (record: AllotmentAdvanceRecord): Record<string, string> => {
    const accounts = getCrewAccounts(record.crewId)
    const byAccount = allotmentByAccountByCrewId[record.crewId] || {}
    const summary = parseAllocationSummary(record.remarks)
    const hasAccountWiseData = Object.keys(byAccount).length > 0
    const next: Record<string, string> = {}

    accounts.forEach((account, index) => {
      const key = getAccountDraftKey(account, index)
      const accountNumberKey = normalizeAccountKey(account.accountNumber)
      let amount = 0

      if (accountNumberKey && Object.prototype.hasOwnProperty.call(byAccount, accountNumberKey)) {
        amount = parseNumberOrZero(byAccount[accountNumberKey])
      } else if (!hasAccountWiseData && Object.prototype.hasOwnProperty.call(summary, index)) {
        amount = parseNumberOrZero(summary[index])
      } else if (!hasAccountWiseData && accounts.length === 1) {
        amount = parseNumberOrZero(record.allotmentAmount)
      } else if (!hasAccountWiseData && Object.keys(summary).length === 0 && index === 0) {
        amount = parseNumberOrZero(record.allotmentAmount)
      }

      next[key] = amount > 0 ? String(amount) : ''
    })

    return next
  }

  const getAccountAmountDraftMap = (record: AllotmentAdvanceRecord): Record<string, string> =>
    accountAllotmentDraftByCrewId[record.crewId] ?? getInitialAccountAmountMap(record)

  const getAccountTotalFromDrafts = (record: AllotmentAdvanceRecord): number => {
    const accountDraftMap = getAccountAmountDraftMap(record)
    return Object.values(accountDraftMap).reduce((sum, value) => sum + parseAmount(value), 0)
  }

  const updateAccountAllotmentDraft = (crewId: number, accountKey: string, value: string) => {
    setAccountAllotmentDraftByCrewId((prev) => ({
      ...prev,
      [crewId]: {
        ...(prev[crewId] || {}),
        [accountKey]: value,
      },
    }))
  }

  const maskAccountNumber = (value?: string | null): string => {
    const raw = (value || '').toString().trim()
    if (!raw) return '-'
    if (raw.length <= 4) return raw
    return `**** **** ${raw.slice(-4)}`
  }

  const getAccountWiseAllottedAmount = (
    record: AllotmentAdvanceRecord,
    account: CrewBankingDetailsRecord
  ): number | undefined => {
    const accounts = getCrewAccounts(record.crewId)
    const accountIndex = accounts.findIndex((acc) => {
      if (account.id && acc.id) return acc.id === account.id
      return accountMatches(acc.accountNumber, account.accountNumber)
    })
    const accountDraftMap = getAccountAmountDraftMap(record)
    const draftKey = getAccountDraftKey(account, accountIndex >= 0 ? accountIndex : 0)
    if (Object.prototype.hasOwnProperty.call(accountDraftMap, draftKey)) {
      return parseAmount(accountDraftMap[draftKey])
    }

    const accountKey = normalizeAccountKey(account.accountNumber)
    if (!accountKey) return undefined

    const byAccount = allotmentByAccountByCrewId[record.crewId] || {}
    if (Object.prototype.hasOwnProperty.call(byAccount, accountKey)) {
      return byAccount[accountKey]
    }

    // Fallback for legacy flow where account splits are stored in remarks summary.
    if (Object.keys(byAccount).length === 0) {
      const accounts = getCrewAccounts(record.crewId)
      const accountIndex = accounts.findIndex((acc) => {
        if (account.id && acc.id) return acc.id === account.id
        return accountMatches(acc.accountNumber, account.accountNumber)
      })
      if (accountIndex >= 0) {
        const summary = parseAllocationSummary(record.remarks)
        if (Object.prototype.hasOwnProperty.call(summary, accountIndex)) {
          return summary[accountIndex]
        }
      }
    }

    if (accounts.length === 1) {
      return record.allotmentAmount
    }

    return 0
  }

  function getTotalAllotmentAmount(record: AllotmentAdvanceRecord): number {
    if (getWorkflowStatus(record) === 'DRAFT') {
      return getAccountTotalFromDrafts(record)
    }
    const byAccount = allotmentByAccountByCrewId[record.crewId]
    if (byAccount && Object.keys(byAccount).length > 0) {
      return Object.values(byAccount).reduce((sum, amount) => sum + parseNumberOrZero(amount), 0)
    }
    return record.allotmentAmount || 0
  }

  function getBalanceCfLastMonth(record: AllotmentAdvanceRecord): number | undefined {
    const draft = inlineDrafts[record.crewId]
    if (draft && draft.balanceBroughtForward !== undefined && draft.balanceBroughtForward !== '') {
      const parsed = Number(draft.balanceBroughtForward)
      if (Number.isFinite(parsed)) return parsed
    }
    if (record.balanceBroughtForward != null && Number.isFinite(Number(record.balanceBroughtForward))) {
      return Number(record.balanceBroughtForward)
    }
    return lastMonthBalanceByCrewId[record.crewId]
  }

  const getCrewAccounts = (crewId: number): CrewBankingDetailsRecord[] => {
    return bankingByCrewId[crewId] || []
  }

  const getActiveAccount = (record: AllotmentAdvanceRecord): CrewBankingDetailsRecord | null => {
    const accounts = getCrewAccounts(record.crewId)
    if (accounts.length === 0) return null

    const allotmentAccount = (record.accountNumber || '').toString().trim()
    if (allotmentAccount) {
      const selected = accounts.find(
        (acc) => (acc.accountNumber || '').toString().trim() === allotmentAccount
      )
      if (selected) return selected
    }

    return accounts[0]
  }

  const getExtraAccountsCount = (record: AllotmentAdvanceRecord): number => {
    const total = getCrewAccounts(record.crewId).length
    return total > 1 ? total - 1 : 0
  }

  const openAccountsModal = (record: AllotmentAdvanceRecord) => {
    setAccountAllotmentDraftByCrewId((prev) => {
      if (prev[record.crewId]) return prev
      return { ...prev, [record.crewId]: getInitialAccountAmountMap(record) }
    })
    setAccountsModalRecord(record)
  }

  const closeAccountsModal = () => {
    setAccountsModalRecord(null)
  }

  const handleSaveAccountsModal = async () => {
    if (!accountsModalRecord) return
    const didSave = await handleSaveInlineRow(accountsModalRecord)
    if (didSave) {
      closeAccountsModal()
    }
  }

  const handleDownloadAllotmentPDF = (record: AllotmentAdvanceRecord) => {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const marginX = 40

      const formatCurrency = (value?: number) =>
        value != null
          ? value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '-'

      const formatMonthLabel = (monthKey?: string): string => {
        if (!monthKey) return '-'
        const [yearStr, monthStr] = monthKey.split('-')
        const year = Number(yearStr)
        const month = Number(monthStr)
        if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return monthKey
        return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
      }

      doc.setFontSize(18)
      doc.text('Crew Allotment Details', marginX, 42)
      doc.setFontSize(10)
      doc.setTextColor(90, 90, 90)

      const filterLabels: string[] = []
      if (record.statementMonth) filterLabels.push(`Month: ${formatMonthLabel(record.statementMonth.slice(0, 7))}`)
      if (record.crewName) filterLabels.push(`Crew: ${record.crewName}`)
      if (record.vesselName) filterLabels.push(`Vessel: ${record.vesselName}`)
      if (filterLabels.length > 0) {
        doc.text(filterLabels.join(' | '), marginX, 60)
      }

      const getWorkflowLabel = (status?: string | null): string => {
        if (!status) return '-'
        const labels: Record<string, string> = {
          'DRAFT': 'Draft',
          'PENDING_MASTER_REVIEW': 'Pending Master Review',
          'MASTER_REVIEWED': 'Master Reviewed',
          'REVIEWED': 'Reviewed',
          'SUBMITTED': 'Submitted',
        }
        return labels[status] || status
      }

      const headers = ['Field', 'Value']
      const body = [
        ['Crew Name', record.crewName || '-'],
        ['Rank', record.rankName || '-'],
        ['Vessel', record.vesselName || '-'],
        ['Month', record.statementMonth ? formatMonthLabel(record.statementMonth.slice(0, 7)) : '-'],
        ['Status', getWorkflowLabel(record.workflowStatus)],
        ['Beneficiary Name', record.beneficiaryName || '-'],
        ['Bank Name', record.bankName || '-'],
        ['Account Number', record.accountNumber || '-'],
        ['Allotment Amount', formatCurrency(getTotalAllotmentAmount(record))],
        ['Wages Earned', formatCurrency(getWagesEarned(record))],
        ['Balance Brought Forward', formatCurrency(getBalanceCfLastMonth(record))],
        ['Carry Forward Amount', formatCurrency(getNetBalanceWages(record))],
        ['Remarks', record.remarks || '-'],
        ['Office Remarks', record.officeRemarks || '-'],
      ]

        ; (doc as any).autoTable({
          startY: 80,
          head: [headers],
          body,
          styles: { fontSize: 10, cellPadding: 5 },
          headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
          bodyStyles: { textColor: [30, 30, 30] },
          alternateRowStyles: { fillColor: [245, 245, 245] },
          margin: { left: marginX, right: marginX },
          theme: 'grid',
          columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 150 },
            1: { cellWidth: 'auto' },
          },
        })

      doc.save(`crew_allotment_${record.crewName}_${record.statementMonth || 'all'}_${new Date().toISOString().split('T')[0]}.pdf`)
      toast.success('PDF downloaded successfully')
    } catch (error) {
      console.error('Error generating PDF:', error)
      showErrorToast(error, 'Failed to generate PDF', 'load')
    }
  }

  function getBalanceCf(record: AllotmentAdvanceRecord) {
    return claimsByCrewId[record.crewId]?.balanceCf ?? 0
  }

  function getNetBalanceWages(record: AllotmentAdvanceRecord) {
    const gross = getBalanceCf(record)
    const allotment = getTotalAllotmentAmount(record)
    return Math.max(0, gross - allotment)
  }

  function getWagesEarned(record: AllotmentAdvanceRecord) {
    return claimsByCrewId[record.crewId]?.wagesEarned ?? record.wagesEarned ?? 0
  }

  function getTotalClaim(record: AllotmentAdvanceRecord) {
    return claimsByCrewId[record.crewId]?.totalClaim ?? 0
  }

  function getTotalDeduction(record: AllotmentAdvanceRecord) {
    return claimsByCrewId[record.crewId]?.totalDeduction ?? 0
  }

  const isRowDirty = (record: AllotmentAdvanceRecord): boolean => {
    const draft = getInlineDraft(record)
    const initial = toDraft(record)
    const currentAccountMap = getAccountAmountDraftMap(record)
    const initialAccountMap = getInitialAccountAmountMap(record)
    const accountKeys = new Set([
      ...Object.keys(currentAccountMap),
      ...Object.keys(initialAccountMap),
    ])
    const accountChanged = Array.from(accountKeys).some(
      (key) => parseAmount(currentAccountMap[key]) !== parseAmount(initialAccountMap[key])
    )
    return (
      parseAmount(draft.carryForwardAmount) !== parseAmount(initial.carryForwardAmount) ||
      parseAmount(draft.wagesEarned) !== parseAmount(initial.wagesEarned) ||
      parseAmount(draft.balanceBroughtForward) !== parseAmount(initial.balanceBroughtForward) ||
      accountChanged
    )
  }

  const getWorkflowStatus = (record: AllotmentAdvanceRecord): AllotmentAdvanceWorkflowStatus =>
    workflowStatusByCrewId[record.crewId] ?? DEFAULT_WORKFLOW_STATUS

  const pendingMasterReviewedRecords = useMemo(() => {
    return sortedRecords.filter((record) => {
      const status = workflowStatusByCrewId[record.crewId] ?? DEFAULT_WORKFLOW_STATUS
      return status === 'MASTER_REVIEWED'
    })
  }, [sortedRecords, workflowStatusByCrewId])

  const totalBalanceWages = useMemo(() => {
    if (!filteredRecords || filteredRecords.length === 0) return 0
    return filteredRecords.reduce((sum, record) => {
      return sum + getNetBalanceWages(record)
    }, 0)
  }, [filteredRecords, claimsByCrewId, accountAllotmentDraftByCrewId, inlineDrafts,
    allotmentByAccountByCrewId, bankingByCrewId, workflowStatusByCrewId])

  const pendingMasterReviewRecords = useMemo(() => {
    return sortedRecords.filter((record) => {
      const status = workflowStatusByCrewId[record.crewId] ?? DEFAULT_WORKFLOW_STATUS
      return status === 'PENDING_MASTER_REVIEW'
    })
  }, [sortedRecords, workflowStatusByCrewId])

  const getWorkflowMeta = (status: AllotmentAdvanceWorkflowStatus) => {
    if (status === 'PENDING_MASTER_REVIEW') {
      return { label: 'Pending Master Review', badgeClass: 'badge-light-info' }
    }
    if (status === 'MASTER_REVIEWED') {
      return { label: 'Ready for Office Review', badgeClass: 'badge-light-warning' }
    }
    if (status === 'SUBMITTED') {
      return { label: 'Ready for Review', badgeClass: 'badge-light-warning' }
    }
    if (status === 'REVIEWED') {
      return { label: 'Reviewed', badgeClass: 'badge-light-success' }
    }
    return { label: 'Draft', badgeClass: 'badge-light-secondary' }
  }

  const renderStatusBadge = (record: AllotmentAdvanceRecord) => {
    const workflowMeta = getWorkflowMeta(getWorkflowStatus(record))

    return (
      <div className='d-flex flex-wrap gap-1'>
        <span className={`badge ${workflowMeta.badgeClass} fw-bold`}>{workflowMeta.label}</span>
      </div>
    )
  }

  const resolveCrewName = (crewId: number) =>
    modalCrewMembers.find((crew) => crew.id === crewId)?.name ||
    crewRoster.find((crew) => crew.id === crewId)?.name

  const handleAddRecord = async (payload: AllotmentAdvanceDraft, pendingFiles: File[] = []) => {
    if (!payload.crewId) {
      toast.error('Crew member is required')
      return
    }

    if (isCrew || roleId === 4) {
      const selfCrewId = resolveSelfCrewId()
      if (!selfCrewId || Number(payload.crewId) !== Number(selfCrewId)) {
        toast.error('You can create allotment only for your own crew profile.')
        return
      }
    }

    if (!ensureClaimsReviewCompleted(Number(payload.crewId), selectedPeriod, 'create allotment')) {
      return
    }

    setIsSaving(true)
    try {
      const periodKey = toPeriodKey(selectedPeriod)
      if (!periodKey) {
        toast.error('Select a valid month before saving crew allotment.')
        return
      }

      const statementMonthStart = toStatementMonthStart(periodKey)
      const existingAllotments = await getCrewAllotments(payload.crewId, periodKey).catch(
        () => [] as CrewAllotmentRecord[]
      )
      const existingInSelectedPeriod = filterAllotmentsByPeriod(existingAllotments, periodKey)
      const existingRecord = existingInSelectedPeriod.find((row) => Number(row.allotmentId) > 0)
      const crewName = resolveCrewName(payload.crewId) || ''

      let targetAllotmentId = 0
      if (existingRecord && existingRecord.allotmentId) {
        targetAllotmentId = existingRecord.allotmentId
        await updateCrewAllotment(payload.crewId, existingRecord.allotmentId, {
          ...existingRecord,
          beneficiaryName: payload.bankingDetails?.nameOfBeneficiary || existingRecord.beneficiaryName || crewName,
          bankAccount: payload.bankingDetails?.accountNumber || existingRecord.bankAccount,
          bankName: payload.bankingDetails?.bankName || existingRecord.bankName,
          amount: payload.allotmentAmount || 0,
          carryForwardAmount: payload.carryForwardAmount,
          wagesEarned: payload.wagesEarned,
          allotmentAmount: payload.allotmentAmount,
          balanceBroughtForward: payload.balanceBroughtForward,
          remarks: payload.remarks,
          statementMonth: statementMonthStart,
        })
      } else {
        const createdAllotment = await createCrewAllotment(payload.crewId, {
          allotmentId: 0,
          beneficiaryName: payload.bankingDetails?.nameOfBeneficiary || crewName,
          bankAccount: payload.bankingDetails?.accountNumber || null,
          bankName: payload.bankingDetails?.bankName || null,
          amount: payload.allotmentAmount || 0,
          carryForwardAmount: payload.carryForwardAmount,
          wagesEarned: payload.wagesEarned,
          allotmentAmount: payload.allotmentAmount,
          balanceBroughtForward: payload.balanceBroughtForward,
          remarks: payload.remarks,
          statementMonth: statementMonthStart,
        })
        targetAllotmentId = createdAllotment.allotmentId || 0
      }

      if (pendingFiles.length > 0 && targetAllotmentId) {
        for (const file of pendingFiles) {
          try {
            await uploadCrewAllotmentAttachment(payload.crewId, targetAllotmentId, file)
          } catch (uploadError) {
            console.error('Error uploading allotment attachment:', uploadError)
            showErrorToast(uploadError, 'Failed to upload allotment attachment', 'save')
          }
        }
      }

      await fetchAllotmentsForCrew(crewRoster)
      setIsAddModalOpen(false)
      toast.success('Allotment saved successfully')
    } catch (error) {
      console.error('Error saving allotment:', error)
      showErrorToast(error, 'Failed to save allotment', 'save')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveInlineRow = async (record: AllotmentAdvanceRecord): Promise<boolean> => {
    if (!canEditAllotmentRecord(record)) {
      toast.error('You do not have permission to edit this allotment')
      return false
    }
    if (!ensureClaimsReviewCompleted(record.crewId, record.statementMonth, 'save allotment')) {
      return false
    }

    const draft = getInlineDraft(record)
    const accounts = getCrewAccounts(record.crewId)
    const accountDraftMap = getAccountAmountDraftMap(record)
    const allocationSummaryParts: string[] = []
    let selectedBankAccount: string | null = null
    let selectedBankName: string | null = null
    let selectedBeneficiaryName: string | null = null
    let totalAllotmentAmount = 0

    accounts.forEach((account, index) => {
      const draftKey = getAccountDraftKey(account, index)
      const amount = parseAmount(accountDraftMap[draftKey])
      if (amount > 0) {
        allocationSummaryParts.push(`Acc ${index + 1}: ${amount}`)
        totalAllotmentAmount += amount
        if (!selectedBankAccount) {
          selectedBankAccount = account.accountNumber || null
          selectedBankName = account.bankName || null
          selectedBeneficiaryName = account.nameOfBeneficiary || null
        }
      }
    })

    if (allocationSummaryParts.length === 0) {
      totalAllotmentAmount = parseAmount(draft.allotmentAmount)
    }

    const remainingBalanceWages = Math.max(0, getBalanceCf(record) - totalAllotmentAmount)
    const effectiveBroughtForward = getBalanceCfLastMonth(record) ?? 0

    const payload = {
      allotmentId: record.id || 0,
      beneficiaryName: selectedBeneficiaryName || record.beneficiaryName || record.crewName,
      bankAccount: selectedBankAccount ?? record.accountNumber ?? null,
      bankName: selectedBankName || record.bankName || null,
      amount: totalAllotmentAmount,
      carryForwardAmount: remainingBalanceWages,
      wagesEarned: getWagesEarned(record),
      allotmentAmount: totalAllotmentAmount,
      balanceBroughtForward: effectiveBroughtForward,
      remarks:
        allocationSummaryParts.length > 0
          ? `Allocations - ${allocationSummaryParts.join(', ')}`
          : '',
      statementMonth: toStatementMonthStart(selectedPeriod) || record.statementMonth || undefined,
    }

    setInlineSavingCrewIds((prev) => ({ ...prev, [record.crewId]: true }))
    try {
      if (record.id && record.id > 0) {
        await updateCrewAllotment(record.crewId, record.id, payload)
      } else {
        await createCrewAllotment(record.crewId, payload)
      }
      await fetchAllotmentsForCrew(crewRoster)
      return true
    } catch (error) {
      console.error('Error saving inline allotment row:', error)
      showErrorToast(error, 'Failed to save allotment', 'update')
      return false
    } finally {
      setInlineSavingCrewIds((prev) => ({ ...prev, [record.crewId]: false }))
    }
  }

  const handleSubmitInlineRow = async (record: AllotmentAdvanceRecord) => {
    if (!canSubmitAllotmentRecord(record)) {
      toast.error('You are not authorized to submit this allotment')
      return
    }
    if (!ensureClaimsReviewCompleted(record.crewId, record.statementMonth, 'submit allotment')) {
      return
    }

    const needsSave = isRowDirty(record) || !(record.id && record.id > 0)
    if (needsSave) {
      const didSave = await handleSaveInlineRow(record)
      if (!didSave) {
        toast.error('Please save the allotment before submitting')
        return
      }
    }
    try {
      const allotments = await getCrewAllotments(record.crewId, selectedPeriod || undefined)
      const latestAllotment = record.id > 0
        ? allotments.find((row) => row.allotmentId === record.id)
        : [...allotments].sort(
            (a, b) => (Number(b.allotmentId) || 0) - (Number(a.allotmentId) || 0)
          )[0]

      if (!latestAllotment?.allotmentId) {
        console.error('No allotment found to submit for crew:', record.crewId)
        toast.error('No allotment found to submit')
        return
      }

      const submitted = await submitCrewAllotment(record.crewId, latestAllotment.allotmentId)
      await fetchAllotmentsForCrew(crewRoster)
      toast.success(submitted.workflowStatus === 'MASTER_REVIEWED'
        ? 'Allotment submitted for office review successfully'
        : 'Allotment submitted for master review successfully')
    } catch (error) {
      console.error('Error submitting allotment row for review:', error)
      showErrorToast(error, 'Failed to submit allotment', 'update')
    }
  }

  const handleOpenMasterReviewModal = (record: AllotmentAdvanceRecord) => {
    if (!canMasterReview) return
    setMasterReviewingRecord(record)
    setIsMasterReviewModalOpen(true)
  }

  const handleCloseMasterReviewModal = () => {
    setIsMasterReviewModalOpen(false)
    setMasterReviewingRecord(null)
  }

  const handleSaveMasterReviewOnly = async (masterRemarks: string) => {
    if (!masterReviewingRecord) return
    setIsMasterReviewSaving(true)
    try {
      await saveCrewAllotmentMasterReviewNote(
        masterReviewingRecord.crewId,
        masterReviewingRecord.id,
        masterRemarks.trim()
      )
      await fetchAllotmentsForCrew(crewRoster)
      handleCloseMasterReviewModal()
      toast.success('Master review note saved successfully')
    } catch (error) {
      console.error('Error saving master review note:', error)
      showErrorToast(error, 'Failed to save master review note', 'save')
    } finally {
      setIsMasterReviewSaving(false)
    }
  }

  const handleApproveMasterReview = async (masterRemarks: string) => {
    if (!masterReviewingRecord) return
    setIsMasterReviewSaving(true)
    try {
      await approveCrewAllotmentMasterReview(
        masterReviewingRecord.crewId,
        masterReviewingRecord.id,
        masterRemarks.trim()
      )
      await fetchAllotmentsForCrew(crewRoster)
      handleCloseMasterReviewModal()
      toast.success('Allotment approved and sent to office for review')
    } catch (error) {
      console.error('Error approving master review:', error)
      showErrorToast(error, 'Failed to approve master review', 'save')
    } finally {
      setIsMasterReviewSaving(false)
    }
  }

  const handleOpenReviewModal = (record: AllotmentAdvanceRecord) => {
    if (!canReviewSubmitted) return
    if (canEditAllotmentRecord(record) && isRowDirty(record)) {
      toast.info('Save your allotment changes before office review')
      return
    }
    setReviewingRecord(record)
    setIsReviewModalOpen(true)
  }

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false)
    setReviewingRecord(null)
  }

  const handleSaveReviewOnly = async (officeRemarks: string) => {
    if (!reviewingRecord) return
    setIsReviewSaving(true)
    try {
      await saveCrewAllotmentReviewNote(
        reviewingRecord.crewId,
        reviewingRecord.id,
        officeRemarks.trim()
      )
      await fetchAllotmentsForCrew(crewRoster)
      handleCloseReviewModal()
      toast.success('Office review note saved successfully')
    } catch (error) {
      console.error('Error saving allotment review note:', error)
      showErrorToast(error, 'Failed to save office review note', 'save')
    } finally {
      setIsReviewSaving(false)
    }
  }

  const handleCloseSatisfactory = async (officeRemarks: string) => {
    if (!reviewingRecord) return
    setIsReviewSaving(true)
    try {
      await closeCrewAllotmentSatisfactory(
        reviewingRecord.crewId,
        reviewingRecord.id,
        officeRemarks.trim()
      )
      await fetchAllotmentsForCrew(crewRoster)
      handleCloseReviewModal()
      toast.success('Allotment closed as satisfactory')
    } catch (error) {
      console.error('Error closing allotment as satisfactory:', error)
      showErrorToast(error, 'Failed to close allotment as satisfactory', 'save')
    } finally {
      setIsReviewSaving(false)
    }
  }

  const handleOpenBulkReviewModal = () => {
    if (!canReviewSubmitted) return
    if (filteredRecords.some((record) => getWorkflowStatus(record) === 'MASTER_REVIEWED' &&
        canEditAllotmentRecord(record) && isRowDirty(record))) {
      toast.info('Save your allotment changes before office review')
      return
    }
    setIsBulkReviewModalOpen(true)
  }

  const handleCloseBulkReviewModal = () => {
    setIsBulkReviewModalOpen(false)
  }

  const handleCloseMasterBulkReviewModal = () => {
    setIsMasterBulkReviewModalOpen(false)
  }

  const handleMasterBulkReviewAll = async (masterRemarks: string, period?: string, file?: File | null) => {
    setIsMasterBulkReviewSaving(true)
    try {
      const vesselId = fixedVesselId ?? (selectedVesselId ? Number(selectedVesselId) : undefined)
      const selectedPeriodKey = period || selectedPeriod || currentMonthStartLocal()
      await bulkMasterReviewAllotments(selectedPeriodKey, vesselId, masterRemarks.trim(), file)
      await fetchAllotmentsForCrew(crewRoster)
      handleCloseMasterBulkReviewModal()
      toast.success('Master bulk review completed successfully')
    } catch (error: any) {
      console.error('Error bulk master reviewing allotments:', error)
      showErrorToast(error, 'Failed to bulk master review allotments', 'save')
    } finally {
      setIsMasterBulkReviewSaving(false)
    }
  }

  const handleBulkReviewAll = async (officeRemarks: string) => {
    setIsBulkReviewSaving(true)
    try {
      const vesselId = fixedVesselId ?? (selectedVesselId ? Number(selectedVesselId) : undefined)
      await bulkReviewAllotments(selectedPeriod, vesselId, officeRemarks.trim())
      await fetchAllotmentsForCrew(crewRoster)
      handleCloseBulkReviewModal()
      toast.success(`Successfully reviewed all allotments for ${formatPeriodLabel(selectedPeriod)}`)
    } catch (error) {
      console.error('Error bulk reviewing allotments:', error)
      showErrorToast(error, 'Failed to bulk review allotments', 'save')
    } finally {
      setIsBulkReviewSaving(false)
    }
  }

  const handleOpenViewModal = (record: AllotmentAdvanceRecord) => {
    setViewRecord({
      ...record,
      balanceCfLastMonth: getBalanceCfLastMonth(record),
      totalClaim: getTotalClaim(record),
      totalDeduction: getTotalDeduction(record),
      totalAllotmentAmount: getTotalAllotmentAmount(record),
      balanceWages: getNetBalanceWages(record),
      wagesEarned: getWagesEarned(record),
    })
    setIsViewModalOpen(true)
  }

  const handleOpenAttachmentsModal = (record: AllotmentAdvanceRecord) => {
    setAttachmentsModalRecord(record)
    setIsAttachmentsModalOpen(true)
  }

  const handleCloseAttachmentsModal = () => {
    setIsAttachmentsModalOpen(false)
    setAttachmentsModalRecord(null)
  }

  return (
    <div className='d-flex flex-column flex-column-fluid'>
      <div className='card flex-column-fluid d-flex flex-column bg-white' style={{ flex: 1 }}>
        <div className='card-header border-0 py-4'>
          <div>
            <h3 className='card-label text-dark fw-bold'>Crew Allotment and Advance</h3>
            <p className='text-muted mb-0 mt-1'>
              Manage allotment and advance details with banking information.
            </p>
          </div>
          <div className='card-toolbar'>
            <div className='d-flex align-items-center gap-2'>
              {canReviewSubmitted && (
                <button
                  type='button'
                  className='btn fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(238, 242, 255)',
                    color: 'rgb(79, 70, 229)',
                    borderColor: 'rgb(224, 231, 255)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.04) 0px 2px 6px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={handleOpenBulkReviewModal}
                  title='Office Review'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <polyline points='9 11 12 14 22 4'></polyline>
                    <path d='M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'></path>
                  </svg>
                  Office Review ({pendingMasterReviewedRecords.length})
                </button>
              )}
              {/* Master Bulk Review button - for PENDING_MASTER_REVIEW records */}
              {(isCrewMaster || isSuperAdmin) && (
                <button
                  type='button'
                  className='btn fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(238, 242, 255)',
                    color: 'rgb(79, 70, 229)',
                    borderColor: 'rgb(224, 231, 255)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.04) 0px 2px 6px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={() => setIsMasterBulkReviewModalOpen(true)}
                  title='Master Bulk Review'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <path d='M18 6L7 17l-5-5'></path>
                    <path d='M22 10l-7.5 7.5L13 16'></path>
                  </svg>
                  Master Bulk Review ({pendingMasterReviewRecords.length})
                </button>
              )}
              {canAddAllotmentAdvance ? (
                <button
                  type='button'
                  className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(29, 78, 216)',
                    borderColor: 'rgb(29, 78, 216)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={openAddModal}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <line x1='12' y1='5' x2='12' y2='19'></line>
                    <line x1='5' y1='12' x2='19' y2='12'></line>
                  </svg>
                  Add Allotment and Advance
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className='card-body py-4 bg-white border-top'>
          <div className='row gx-3 gy-3 align-items-end'>
            <div className='col-md-3'>
              <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                {useCrewDropdownFilter ? 'Crew' : 'Search'}
              </label>
              {useCrewDropdownFilter ? (
                <select
                  className='form-select'
                  value={selectedCrewId}
                  onChange={(event) =>
                    setSelectedCrewId(event.target.value ? Number(event.target.value) : '')
                  }
                >
                  <option value=''>All Crew</option>
                  {crewFilterOptions.map((crew) => (
                    <option key={crew.id} value={crew.id}>
                      {crew.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type='text'
                  className='form-control'
                  placeholder='Search crew or rank'
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              )}
            </div>

            <div className='col-md-2'>
              <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>
                Month / Year
              </label>
              <input
                type='month'
                className='form-control'
                value={selectedPeriod}
                onChange={(event) => handleSelectedPeriodChange(event.target.value)}
              />
            </div>

            <div className='col-md-auto d-flex align-items-end'>
              <div
                className='bg-light-primary border border-primary border-dashed rounded px-3 py-2 d-flex align-items-center gap-2'
                style={{ height: '40px' }}
                title='Total Balance Wages for filtered crew members'
              >
                <span className='text-gray-600 fs-8 fw-semibold text-nowrap'>Total Balance Wages:</span>
                <span className='text-primary fw-bolder fs-7 text-nowrap'>
                  ${formatCurrency(totalBalanceWages)}
                </span>
              </div>
            </div>

            <div className='col-md-auto ms-auto d-flex align-items-end gap-2'>
              {canOpenSalarySlip ? (
                <button
                  type='button'
                  className='btn fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(239, 246, 255)',
                    color: 'rgb(29, 78, 216)',
                    borderColor: 'rgb(219, 234, 254)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.04) 0px 2px 6px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={openSalarySlipModal}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <path d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z'></path>
                    <polyline points='14 2 14 8 20 8'></polyline>
                    <line x1='16' y1='13' x2='8' y2='13'></line>
                    <line x1='16' y1='17' x2='8' y2='17'></line>
                    <line x1='10' y1='9' x2='8' y2='9'></line>
                  </svg>
                  Salary Slip
                </button>
              ) : null}
              {canOpenPortageBill ? (
                <button
                  type='button'
                  className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(29, 78, 216)',
                    borderColor: 'rgb(29, 78, 216)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={() => setIsPortageBillModalOpen(true)}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'></path>
                    <polyline points='14 2 14 8 20 8'></polyline>
                    <line x1='16' y1='13' x2='8' y2='13'></line>
                    <line x1='16' y1='17' x2='8' y2='17'></line>
                    <polyline points='10 9 9 9 8 9'></polyline>
                  </svg>
                  Portage Bill
                </button>
              ) : null}
              {canExportAllotment && (
                <button
                  type='button'
                  className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(220, 38, 38)',
                    borderColor: 'rgb(220, 38, 38)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={() => setIsExportModalOpen(true)}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
                    <polyline points='7 10 12 15 17 10'></polyline>
                    <line x1='12' y1='15' x2='12' y2='3'></line>
                  </svg>
                  Export PDF
                </button>
              )}
            </div>
          </div>
        </div>

        <div className='card-body py-4 bg-white border-top'>
          <div className='report-table table-responsive' onWheel={handleTableHorizontalWheelScroll}>
            <table className='table table-bordered align-middle'>
              <thead className='table-header text-start'>
                <tr>
                  <th className='text-nowrap'>Sr/No</th>
                  <th className='text-nowrap'>
                    <div className='d-flex align-items-center'>
                      <span className='me-1'>Crew</span>
                      <button
                        type='button'
                        className='btn btn-link p-0 m-0 pb-1'
                        onClick={() => handleSort('crewName')}
                      >
                        <KTSVG
                          path={`/media/map/sort-col-${sortConfig.key === 'crewName'
                            ? sortConfig.direction === 'asc'
                              ? 'up-black'
                              : 'down-black'
                            : 'grey'
                            }.svg`}
                          className='svg-icon-3'
                        />
                      </button>
                    </div>
                  </th>
                  <th className='text-nowrap'>
                    <div className='d-flex align-items-center'>
                      <span className='me-1'>Rank</span>
                      <button
                        type='button'
                        className='btn btn-link p-0 m-0 pb-1'
                        onClick={() => handleSort('rankName')}
                      >
                        <KTSVG
                          path={`/media/map/sort-col-${sortConfig.key === 'rankName'
                            ? sortConfig.direction === 'asc'
                              ? 'up-black'
                              : 'down-black'
                            : 'grey'
                            }.svg`}
                          className='svg-icon-3'
                        />
                      </button>
                    </div>
                  </th>
                  {showVesselAndPeriodColumns && <th className='text-nowrap'>Vessel</th>}
                  {showVesselAndPeriodColumns && <th className='text-nowrap'>Month / Year</th>}
                  <th className='text-nowrap'>Beneficiary Account Name</th>
                  <th className='text-nowrap'>Account Details</th>
                  <th className='text-nowrap' style={amountHeaderStyle}>
                    Balance C/F Last Month
                  </th>
                  <th className='text-nowrap' style={amountHeaderStyle}>
                    Wages Earned
                  </th>
                  <th className='text-nowrap' style={amountHeaderStyle}>
                    Total Claim
                  </th>
                  <th className='text-nowrap' style={amountHeaderStyle}>
                    Total Deduction
                  </th>
                  <th className='text-nowrap' style={amountHeaderStyle}>
                    Total Allotment Amount
                  </th>
                  <th className='text-nowrap' style={amountHeaderStyle}>
                    Balance Wages
                  </th>
                  <th className='text-nowrap' style={{ minWidth: '160px', maxWidth: '220px' }}>Master Remarks</th>
                  <th className='text-nowrap' style={{ minWidth: '160px', maxWidth: '220px' }}>Office Remarks</th>
                  <th className='text-nowrap'>Status</th>
                  <th className='text-nowrap text-center'>Actions</th>
                </tr>
              </thead>
              <tbody className='table-body text-start'>
                {isLoading ? (
                  <TableSkeleton rows={rowsPerPage || 10} cols={showVesselAndPeriodColumns ? 17 : 15} />
                ) : currentRecords.length === 0 ? (
                  <tr>
                    <td
                      colSpan={showVesselAndPeriodColumns ? 17 : 15}
                      className='text-center text-muted py-5'
                    >
                      No allotment records found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  currentRecords.map((record, index) => {
                    const rowSaving = Boolean(inlineSavingCrewIds[record.crewId])
                    const workflowStatus = getWorkflowStatus(record)
                    const canEditRow = canEditAllotmentRecord(record)
                    const masterRemarks = masterRemarksByCrewId[record.crewId] || '-'
                    const officeRemarks = reviewRemarksByCrewId[record.crewId] || '-'
                    const activeAccount = getActiveAccount(record)
                    const extraAccountsCount = getExtraAccountsCount(record)
                    const currencySymbol = getCurrencySymbol(
                      resolveCurrencyCode(record.salaryCurrency, record.salaryOtherCurrency)
                    )
                    return (
                      <tr key={record.crewId}>
                        <td>{indexOfFirstRecord + index + 1}</td>
                        <td>{record.crewName}</td>
                        <td>{record.rankName || '-'}</td>
                        {showVesselAndPeriodColumns && <td>{record.vesselName || '-'}</td>}
                        {showVesselAndPeriodColumns && (
                          <td>{formatStatementMonth(record.statementMonth)}</td>
                        )}
                        <td>{activeAccount?.nameOfBeneficiary || record.beneficiaryName || '-'}</td>
                        <td>
                          <div className='d-flex flex-column align-items-start gap-1'>
                            <span className='text-dark fw-semibold'>
                              {maskAccountNumber(
                                activeAccount?.accountNumber || record.accountNumber || ''
                              )}
                            </span>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary py-1 px-2 text-nowrap'
                              onClick={() => openAccountsModal(record)}
                            >
                              Account Info
                              {extraAccountsCount > 0 ? ` (+${extraAccountsCount})` : ''}
                            </button>
                          </div>
                        </td>
                        <td>
                          <div
                            className='input-group input-group-sm flex-nowrap'
                            style={amountGroupStyle}
                          >
                            <span className='input-group-text' style={amountPrefixStyle}>
                              {currencySymbol}
                            </span>
                            <input
                              type='text'
                              className='form-control form-control-sm form-control-solid fw-bold'
                              value={
                                getBalanceCfLastMonth(record) == null
                                  ? '-'
                                  : formatCurrency(getBalanceCfLastMonth(record))
                              }
                              readOnly
                            />
                          </div>
                        </td>
                        <td>
                          <div
                            className='input-group input-group-sm flex-nowrap'
                            style={amountGroupStyle}
                          >
                            <span className='input-group-text' style={amountPrefixStyle}>
                              {currencySymbol}
                            </span>
                            <input
                              type='text'
                              className='form-control form-control-sm form-control-solid fw-bold'
                              value={formatCurrency(getWagesEarned(record))}
                              readOnly
                            />
                          </div>
                        </td>
                        <td>
                          <div
                            className='input-group input-group-sm flex-nowrap'
                            style={amountGroupStyle}
                          >
                            <span className='input-group-text' style={amountPrefixStyle}>
                              {currencySymbol}
                            </span>
                            <input
                              type='text'
                              className='form-control form-control-sm form-control-solid fw-bold'
                              value={formatCurrency(getTotalClaim(record))}
                              readOnly
                            />
                          </div>
                        </td>
                        <td>
                          <div
                            className='input-group input-group-sm flex-nowrap'
                            style={amountGroupStyle}
                          >
                            <span className='input-group-text' style={amountPrefixStyle}>
                              {currencySymbol}
                            </span>
                            <input
                              type='text'
                              className='form-control form-control-sm form-control-solid fw-bold'
                              value={formatCurrency(getTotalDeduction(record))}
                              readOnly
                            />
                          </div>
                        </td>
                        <td>
                          <div
                            className='input-group input-group-sm flex-nowrap'
                            style={amountGroupStyle}
                          >
                            <span className='input-group-text' style={amountPrefixStyle}>
                              {currencySymbol}
                            </span>
                            <input
                              type='text'
                              className='form-control form-control-sm form-control-solid fw-bold'
                              value={formatCurrency(getTotalAllotmentAmount(record))}
                              readOnly
                            />
                          </div>
                        </td>
                        <td>
                          <div
                            className='input-group input-group-sm flex-nowrap'
                            style={amountGroupStyle}
                          >
                            <span className='input-group-text' style={amountPrefixStyle}>
                              {currencySymbol}
                            </span>
                            <input
                              type='text'
                              className='form-control form-control-sm form-control-solid fw-bold'
                              value={formatCurrency(getNetBalanceWages(record))}
                              readOnly
                            />
                          </div>
                        </td>
                        <td>
                          <RemarksCell
                            text={masterRemarks !== '-' ? masterRemarks : ''}
                            title='Master Remarks'
                            subtitle={record.crewName || ''}
                            onOpenModal={(title, subtitle, text) => {
                              setRemarksModal({ visible: true, title, subtitle, text })
                            }}
                          />
                        </td>
                        <td>
                          <RemarksCell
                            text={officeRemarks !== '-' ? officeRemarks : ''}
                            title='Office Remarks'
                            subtitle={record.crewName || ''}
                            onOpenModal={(title, subtitle, text) => {
                              setRemarksModal({ visible: true, title, subtitle, text })
                            }}
                          />
                        </td>
                        <td>{renderStatusBadge(record)}</td>
                        <td className='text-center text-nowrap'>
                          <button
                            type='button'
                            className='btn btn-icon btn-sm me-1'
                            onClick={() => handleOpenViewModal(record)}
                            title='View'
                          >
                            <KTSVG
                              path='/media/map/ph_eye.svg'
                              className='svg-icon-3 text-primary'
                            />
                          </button>
                          {canSubmitAllotmentRecord(record) && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => handleSubmitInlineRow(record)}
                              disabled={rowSaving}
                              title='Submit for review'
                            >
                              Submit
                            </button>
                          )}
                          {workflowStatus === 'PENDING_MASTER_REVIEW' && canMasterReview && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-info'
                              onClick={() => handleOpenMasterReviewModal(record)}
                              title='Master Review'
                            >
                              Master Review
                            </button>
                          )}
                          {workflowStatus === 'MASTER_REVIEWED' && canReviewSubmitted && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-warning'
                              onClick={() => handleOpenReviewModal(record)}
                              title='Office Review'
                            >
                              Office Review
                            </button>
                          )}
                          {canDownloadAllotment && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={() => handleDownloadAllotmentPDF(record)}
                              title='Download PDF'
                            >
                              <KTSVG path='/media/icons/duotune/files/fil021.svg' className='svg-icon-2' />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className='d-flex justify-content-between align-items-center mt-4'>
            <div className='d-flex align-items-center gap-2'>
              <label className='text-muted fs-7'>Rows per page</label>
              <select
                className='form-select form-select-sm'
                value={rowsPerPage}
                onChange={(event) => {
                  setRowsPerPage(Number(event.target.value))
                  setCurrentPage(1)
                }}
                style={{ width: '90px' }}
              >
                {[5, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <button
                className='btn btn-sm btn-light'
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className='text-muted fs-7'>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className='btn btn-sm btn-light'
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddAllotmentAdvanceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddRecord}
        isSaving={isSaving}
        selectedPeriod={selectedPeriod}
        crewMembers={modalEligibleCrewMembers}
        rankMap={rankMap}
        companies={companies}
        companyAdmins={companyAdmins}
        vessels={vessels}
        selectedCompanyGroupId={modalCompanyGroupId}
        selectedCompanyAdminId={modalCompanyAdminId}
        selectedVesselId={modalVesselId}
        showCompanyFilter={showCompanyFilter}
        showSubCompanyFilter={shouldShowModalSubCompanyFilterField}
        showVesselFilter={showVesselFilter}
        lockCrewSelection={lockAddModalCrewSelection}
        onCompanyGroupChange={(next) => {
          setModalCompanyGroupId(next)
          setModalCompanyAdminId('')
          setModalVesselId('')
        }}
        onCompanyAdminChange={(next) => {
          setModalCompanyAdminId(next)
          setModalVesselId('')
        }}
        onVesselChange={(next) => setModalVesselId(next)}
      />

      <SalarySlipModal
        isOpen={isSalarySlipModalOpen}
        onClose={() => setIsSalarySlipModalOpen(false)}
        selectedPeriod={selectedPeriod}
        crewOptions={salarySlipCrewOptions}
        defaultCrewId={selectedCrewId ? Number(selectedCrewId) : salarySlipCrewOptions[0]?.crewId}
      />

      <PortageBillModal
        isOpen={isPortageBillModalOpen}
        onClose={() => setIsPortageBillModalOpen(false)}
        selectedPeriod={selectedPeriod}
        initialFilters={{
          companyGroupId: selectedCompanyGroupId ? Number(selectedCompanyGroupId) : null,
          companyAdminId: selectedCompanyAdminId ? Number(selectedCompanyAdminId) : null,
          vesselId: selectedVesselId ? Number(selectedVesselId) : null,
          crewId: selectedCrewId ? Number(selectedCrewId) : null,
        }}
        currentUser={currentUser}
        isSuperAdmin={isSuperAdmin}
        isCompanyAdmin={isCompanyAdmin}
        isCompanyGroupAdmin={isCompanyGroupAdmin}
        isOperator={isOperator}
        isCrewMaster={isCrewMaster}
        vessels={vessels}
        allRecords={sortedRecords}
        getBalanceCfLastMonth={getBalanceCfLastMonth}
        getWagesEarned={getWagesEarned}
        getTotalClaim={getTotalClaim}
        getTotalDeduction={getTotalDeduction}
        getTotalAllotmentAmount={getTotalAllotmentAmount}
        getBalanceCf={getBalanceCf}
      />

      <ViewAllotmentAdvanceModal
        visible={isViewModalOpen}
        record={viewRecord}
        onClose={() => setIsViewModalOpen(false)}
      />

      <AllotmentAdvanceMasterReviewModal
        visible={isMasterReviewModalOpen}
        record={masterReviewingRecord}
        status={masterReviewingRecord ? getWorkflowStatus(masterReviewingRecord) : DEFAULT_WORKFLOW_STATUS}
        initialRemarks={masterReviewingRecord ? masterRemarksByCrewId[masterReviewingRecord.crewId] : ''}
        onClose={handleCloseMasterReviewModal}
        onSave={handleSaveMasterReviewOnly}
        onApprove={handleApproveMasterReview}
        isLoading={isMasterReviewSaving}
      />

      <AllotmentAdvanceReviewModal
        visible={isReviewModalOpen}
        record={reviewingRecord}
        status={reviewingRecord ? getWorkflowStatus(reviewingRecord) : DEFAULT_WORKFLOW_STATUS}
        initialRemarks={reviewingRecord?.officeRemarks || ''}
        onClose={handleCloseReviewModal}
        onSave={handleSaveReviewOnly}
        onCloseSatisfactory={handleCloseSatisfactory}
        isLoading={isReviewSaving}
      />

      <AllotmentBulkReviewModal
        visible={isBulkReviewModalOpen}
        records={pendingMasterReviewedRecords}
        period={formatPeriodLabel(selectedPeriod)}
        onClose={handleCloseBulkReviewModal}
        onReviewAll={handleBulkReviewAll}
        isLoading={isBulkReviewSaving}
      />

      <AllotmentMasterBulkReviewModal
        visible={isMasterBulkReviewModalOpen}
        records={pendingMasterReviewRecords}
        period={
          selectedPeriod
            ? formatPeriodLabel(selectedPeriod)
            : pendingMasterReviewRecords[0]?.statementMonth
              ? formatPeriodLabel(pendingMasterReviewRecords[0].statementMonth.slice(0, 7))
              : 'Selected Month'
        }
        onClose={handleCloseMasterBulkReviewModal}
        onReviewAll={handleMasterBulkReviewAll}
        isLoading={isMasterBulkReviewSaving}
      />

      <CrewAllotmentExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        initialFilters={{
          companyGroupId: selectedCompanyGroupId ? Number(selectedCompanyGroupId) : null,
          companyAdminId: selectedCompanyAdminId ? Number(selectedCompanyAdminId) : null,
          vesselId: selectedVesselId ? Number(selectedVesselId) : null,
          crewId: selectedCrewId ? Number(selectedCrewId) : null,
          month: selectedPeriod || null,
        }}
        currentUser={currentUser}
        isSuperAdmin={isSuperAdmin}
        isCompanyAdmin={isCompanyAdmin}
        isCompanyGroupAdmin={isCompanyGroupAdmin}
        isOperator={isOperator}
        isCrewMaster={isCrewMaster}
        isCrew={isCrew}
        vessels={vessels}
        allRecords={sortedRecords}
        records={sortedRecords}
      />

      <CrewClaimAttachmentsModal
        visible={isAttachmentsModalOpen}
        title={`Attachments - ${attachmentsModalRecord?.crewName || 'Allotment'}`}
        subtitle={attachmentsModalRecord?.rankName || null}
        loading={false}
        viewPermissionCode="READ_CREW_ALLOTMENT"
        downloadPermissionCode="READ_CREW_ALLOTMENT"
        items={(attachmentsModalRecord?.attachments || []).map((att) => ({
          id: att.id,
          fileName: att.fileName,
          viewUrl: crewAllotmentAttachmentViewUrl(
            attachmentsModalRecord!.crewId,
            attachmentsModalRecord!.id,
            att.id
          ),
          downloadUrl: crewAllotmentAttachmentDownloadUrl(
            attachmentsModalRecord!.crewId,
            attachmentsModalRecord!.id,
            att.id
          ),
          fileType: att.fileType || undefined,
          fileSize: att.fileSize ?? undefined,
          mimeType: att.mimeType,
        }))}
        onClose={handleCloseAttachmentsModal}
        onViewFile={(item) => {
          setFileModal({
            visible: true,
            title: item.fileName,
            fileName: item.fileName,
            viewUrl: item.viewUrl,
            downloadUrl: item.downloadUrl,
          })
        }}
      />

      <FileViewerModal
        visible={fileModal.visible}
        title={fileModal.title}
        fileName={fileModal.fileName}
        viewUrl={fileModal.viewUrl}
        downloadUrl={fileModal.downloadUrl}
        onClose={() => setFileModal((prev) => ({ ...prev, visible: false }))}
      />

      {accountsModalRecord &&
        (() => {
          const modalAccounts = getCrewAccounts(accountsModalRecord.crewId)
          const modalTotalAllottedAmount = getAccountTotalFromDrafts(accountsModalRecord)
          const modalBalanceWages = getBalanceCf(accountsModalRecord) - modalTotalAllottedAmount
          const canSaveAccountsModal = canEditAllotmentRecord(accountsModalRecord)
          const isAccountsModalSaving = Boolean(inlineSavingCrewIds[accountsModalRecord.crewId])
          const isAccountsModalDirty = isRowDirty(accountsModalRecord)
          const showAccountOtherCurrencyColumn = modalAccounts.some((account) => {
            const otherCurrency = (account.otherCurrency || account.salaryOtherCurrency || '')
              .toString()
              .trim()
            if (otherCurrency) return true

            const accountCurrency = (account.accountCurrency || account.salaryCurrency || '')
              .toString()
              .trim()
              .toUpperCase()
            return accountCurrency === 'OTHER'
          })

          return (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1050,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 1040,
                }}
                onClick={closeAccountsModal}
              />
              <div
                style={{
                  position: 'relative',
                  zIndex: 1051,
                  width: '95vw',
                  height: '90vh',
                  backgroundColor: 'white',
                  borderRadius: '0.475rem',
                  boxShadow: '0 0 50px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{ padding: '1.5rem', borderBottom: '1px solid #e9ecef', flex: '0 0 auto' }}
                >
                  <h5 className='modal-title'>Bank Accounts - {accountsModalRecord.crewName}</h5>
                  <button
                    type='button'
                    className='btn-close'
                    aria-label='Close'
                    onClick={closeAccountsModal}
                    style={{ position: 'absolute', top: '1rem', right: '1rem' }}
                  />
                </div>
                <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '2rem' }}>
                  {modalAccounts.length === 0 ? (
                    <p className='text-muted mb-0'>No banking accounts available.</p>
                  ) : (
                    <>
                      <div
                        style={{ width: '100%', overflowX: 'auto' }}
                        onWheel={handleTableHorizontalWheelScroll}
                      >
                        <table
                          className='table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4'
                          style={{ width: '100%', minWidth: '1350px', marginBottom: '0' }}
                        >
                          <thead>
                            <tr className='fw-bold text-muted'>
                              <th className='min-w-100px'>Account</th>
                              <th className='min-w-200px'>Beneficiary</th>
                              <th className='min-w-150px'>Account Details</th>
                              <th className='min-w-150px'>Allotted Amount</th>
                              <th className='min-w-200px'>Bank</th>
                              <th className='min-w-160px'>Branch Name</th>
                              <th className='min-w-140px'>Branch Code</th>
                              <th className='min-w-180px'>Bank Code</th>
                              <th className='min-w-180px'>Bank Code Value</th>
                              <th className='min-w-220px'>Bank Address</th>
                              <th className='min-w-120px'>Account Currency</th>
                              {showAccountOtherCurrencyColumn ? (
                                <th className='min-w-120px'>Account Other Currency</th>
                              ) : null}
                              <th className='min-w-180px'>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalAccounts.map((account, index) => {
                              const code = resolveBankCode(
                                account.ifscRtgsType,
                                account.ifscRtgsCode,
                                account.swiftIandi
                              )
                              const canEditAccountAmount = canEditAllotmentRecord(accountsModalRecord)
                              const accountDraftMap = getAccountAmountDraftMap(accountsModalRecord)
                              const accountDraftKey = getAccountDraftKey(account, index)
                              const readonlyAmount = getAccountWiseAllottedAmount(
                                accountsModalRecord,
                                account
                              )

                              return (
                                <tr key={account.id || index}>
                                  <td>
                                    <span className='text-dark fw-bold fs-6'>Acc {index + 1}</span>
                                  </td>
                                  <td className='text-dark fw-bold'>
                                    {account.nameOfBeneficiary || '-'}
                                  </td>
                                  <td className='text-muted fw-semibold'>
                                    {maskAccountNumber(account.accountNumber)}
                                  </td>
                                  <td className='text-dark fw-bold'>
                                    <input
                                      type='number'
                                      min={0}
                                      step='0.01'
                                      className={`form-control form-control-sm fw-bold ${canEditAccountAmount ? '' : 'form-control-solid'
                                        }`}
                                      value={
                                        canEditAccountAmount
                                          ? accountDraftMap[accountDraftKey] ?? ''
                                          : readonlyAmount == null
                                            ? ''
                                            : String(readonlyAmount)
                                      }
                                      onChange={(event) =>
                                        updateAccountAllotmentDraft(
                                          accountsModalRecord.crewId,
                                          accountDraftKey,
                                          event.target.value
                                        )
                                      }
                                      onWheel={blurNumberInputOnWheel}
                                      onKeyDown={preventNumberInputArrowStep}
                                      readOnly={!canEditAccountAmount}
                                    />
                                  </td>
                                  <td className='text-dark fw-bold'>{account.bankName || '-'}</td>
                                  <td className='text-dark fw-bold'>{account.branchName || '-'}</td>
                                  <td className='text-dark fw-bold'>{account.branchCode || '-'}</td>
                                  <td className='text-dark fw-bold'>{code.label}</td>
                                  <td className='text-muted fw-semibold'>{code.value}</td>
                                  <td className='text-dark fw-bold'>
                                    {account.bankAddress || '-'}
                                  </td>
                                  <td>
                                    <span className='badge badge-light-primary fw-bold'>
                                      {(account.accountCurrency || account.salaryCurrency || '-')
                                        .toString()
                                        .toUpperCase()}
                                    </span>
                                  </td>
                                  {showAccountOtherCurrencyColumn ? (
                                    <td className='text-dark fw-bold'>
                                      {account.otherCurrency || account.salaryOtherCurrency || '-'}
                                    </td>
                                  ) : null}
                                  <td className='text-dark fw-bold'>{account.remarks || '-'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className='d-flex justify-content-end mt-4'>
                        <div style={{ width: '300px' }}>
                          <label className='form-label fw-semibold fs-6 mb-2 text-dark'>
                            Balance Wages
                          </label>
                          <input
                            type='text'
                            className='form-control form-control-solid fw-bold'
                            value={formatCurrency(modalBalanceWages)}
                            readOnly
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div
                  style={{
                    padding: '1.5rem',
                    borderTop: '1px solid #e9ecef',
                    flex: '0 0 auto',
                    textAlign: 'right',
                  }}
                >
                  {canSaveAccountsModal ? (
                    <button
                      type='button'
                      className='btn btn-light-primary me-2'
                      onClick={handleSaveAccountsModal}
                      disabled={isAccountsModalSaving || !isAccountsModalDirty}
                    >
                      {isAccountsModalSaving ? 'Saving...' : 'Save'}
                    </button>
                  ) : null}
                  <button type='button' className='btn btn-light' onClick={closeAccountsModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

      <RemarksModal
        visible={remarksModal.visible}
        title={remarksModal.title}
        subtitle={remarksModal.subtitle}
        remarksText={remarksModal.text}
        onClose={() => setRemarksModal(prev => ({ ...prev, visible: false }))}
      />

      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: '5rem' }}
      />
    </div>
  )
}

export default CrewAllotment
